/* eslint-disable @typescript-eslint/no-dynamic-delete */
// @ts-strict-ignore
import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { AuthService } from 'rainbow-web-sdk/services/core/auth.service';
import { ErrorHelperService } from 'rainbow-web-sdk/services/utilities/errorHelper.service';
import { Subject, Subscription, filter } from "rxjs";
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService, ContactServiceInnerEvents } from 'rainbow-web-sdk/services/core/contact.service';
import { Contact, UserRole } from 'rainbow-web-sdk/models/contact.model';
import { TelephonyForward } from 'rainbow-web-sdk/models/telephonyForward.model';
import { PersonalRoutine } from 'rainbow-web-sdk/models/personalRoutine.model';
import { Service } from 'rainbow-web-sdk/services/service';
import type { TelephonyService } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { TELEPHONY_SVC } from 'rainbow-web-sdk/services/telephony/telephony.service';
import { PersonalRoutinesService } from 'rainbow-web-sdk/services/personalRoutines/personal-routines.service';
import { UserInfoService } from 'rainbow-web-sdk/services/core/userInfo.service';
import { ProfileService } from 'rainbow-web-sdk/services/core/profile.service';
import { WebrtcP2PService, WebrtcP2PServiceEvents } from '../webrtc/webrtcP2P.service';
import { CallEvents, CallMedia, CallRB, CallStatus, CallType } from 'rainbow-web-sdk/models/call.model';
import { ConversationRB } from 'rainbow-web-sdk/models/conversation.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { BUBBLE_CONFERENCE_SVC, BubbleConferenceServiceRB, WebConferenceServiceInnerEvents } from '../conference/bubbleConference.service';
import { ConversationServiceEvents, ConversationServiceRB } from '../conversation/conversation.service';
import { MPCallState, WEBRTC_GATEWAY_SVC } from '../webrtcGateway/webrtcGateway.service';
import type { WebrtcGatewayService } from '../webrtcGateway/webrtcGateway.service';
import { SettingsService } from 'rainbow-web-sdk/services/core/settings.service';
import { $msg } from 'strophe.js';

export const SUPERVISION_SVC = 'SupervisionService';

export interface SupervisionGroupMember {
    userId: string;
    roles: string[]; // array of roles. Possible roles are "supervisor"/"supervisor_admin"/"supervised"
    inOutPermission: string; // Possible values: "allowed"/"locked"
    status: string; // log on/off status in the group. Possible values : "active"/"idle"
    isDelegationEnabled: boolean; // Indicates wether the manager's call delegation is activated or not in a manager/assistant supervision group
}

export interface SupervisionGroup {
    id: string; // Group id
    name: string; // Group name
    type: string; // Group type. Possible values: "default"/"mutualAid"/"manager_assistant"
    isEmptyAllowed: boolean; // Indicate wether the last user has the right to log off from group or not
    members: SupervisionGroupMember[]; // Array of group members
    supervisorUsers: string[]; // Array of supervisor user identifiers
    supervisedUsers: string[]; // Array of supervised user identifiers
    supervisorUserContacts: Contact[]; // Array of supervisor user contacts
    supervisedUserContacts: Contact[]; // Array of supervised user contacts
    isMyRoleSupervisor: boolean; // Am I a supervisor user in this group?
    isMyRoleSupervisorAdmin: boolean; // Am I a supervisor admin user in this group?
    isMyRoleSupervised: boolean; // Am I a supervised user in this group?
    avatar: string; // Computed group avatar
    nbIncomingRingingCalls: number; // nb of incoming ringing calls inside the group
}

export interface CallInfo {
    callId: string; // Call id
    callState: string; // Call state
    callerNumber: string; // Caller number
    callerName: string; // Caller displayname
    callType?: string; // type of call : hg/ccq/sfu (only sipwise for now)
    callerJid?: string; // the jid of the distant user (if available) or the MP
    resource?: string; // the ressource of the distant user that is in the call (to build the full jid)
    media?: string; //the current media (Audio or video) in the call
    type?: string; //the type of the call endpoint (p2p or sfu or tel)
    notified?: boolean; // Indicates wether the supervised call has been notified by audio to the supervisor or not
    sid?: string; //the remote jingle session ID
}

export interface RingingSupervisedCall {
    callInfo: CallInfo; // Call info of the ringing call
    supervisedContact: Contact; // supervised user which is ringing
    groupsAvatars: string[]; // Avatars of  supervision groups containing the supervised ringing user
    groupsNames: string[]; // Names of  supervision groups containing the supervised ringing user
    groupsIds: string[]; // Ids of supervision groups containing the supervised ringing user
}

export interface SupervisionSettings {
    disableSupervisionCallNotificationsOnMobiles: boolean;
}

export class SupervisionService extends Service {

    /**
     * @public
     * @event ON_SUPERVISOR_ROLE_UPDATED
     * @description This RB event is sent when the user supervisor role has changed.
     */
    public static ON_SUPERVISOR_ROLE_UPDATED = 'ON_SUPERVISOR_ROLE_UPDATED';

    /**
     * @public
     * @event ON_SUPERVISION_CONVERSATIONS_UPDATED
     * @param {string} state the global state for the supervision
     * @param {CallInfo[]} callInfos [optional] the information for all calls currently in supervision (could be empty)
     * @description This RB event is sent when there's update on the supervision conversation (for call shift)
     */
    public static ON_SUPERVISION_CONVERSATIONS_UPDATED = 'ON_SUPERVISION_CONVERSATIONS_UPDATED';

    /**
     * @public
     * @event ON_SUPERVISION_RINGING_CONTACTS_UPDATED
     * @param {RingingSupervisedCall[]} ringingSupervisedCallList array of ringing calls on supervised contacts
     * @description This RB event is sent when there's update on the ringing calls on supervised contact's array
     */
    public static ON_SUPERVISION_RINGING_CONTACTS_UPDATED = 'ON_SUPERVISION_RINGING_CONTACTS_UPDATED';

    /**
     * @public
     * @event ON_SUPERVISION_RINGING_SUPERVISED_CALL_CREATED
     * @param {RingingSupervisedCall} ringingSupervisedCall new ringing call on a supervised contact
     * @description This RB event is sent when there's a new ringing call on a supervised contact
     */
    public static ON_SUPERVISION_RINGING_SUPERVISED_CALL_CREATED = 'ON_SUPERVISION_RINGING_SUPERVISED_CALL_CREATED';

    /**
     * @public
     * @event ON_SUPERVISION_RINGING_SUPERVISED_CALL_REMOVED
     * @param {RingingSupervisedCall} ringingSupervisedCall removed ringing call on supervised contact
     * @description This RB event is sent when a ringing call stops ringing on a supervised contact
     */
    public static ON_SUPERVISION_RINGING_SUPERVISED_CALL_REMOVED = 'ON_SUPERVISION_RINGING_SUPERVISED_CALL_REMOVED';

    public started: boolean = false;
    private mainService: MainService = MainService.getInstance();
    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private authService: AuthService = AuthService.getInstance();
    private errorHelperService: ErrorHelperService = ErrorHelperService.getInstance();
    private telephonyService: TelephonyService = this.mainService.inject<TelephonyService>(TELEPHONY_SVC);
    private webrtcGatewayService: WebrtcGatewayService = this.mainService.inject(WEBRTC_GATEWAY_SVC);
    private personalRoutinesService: PersonalRoutinesService = PersonalRoutinesService.getInstance();
    private userInfoService: UserInfoService = UserInfoService.getInstance();
    private profileService: ProfileService = ProfileService.getInstance();
    private webrtcService: WebrtcP2PService = WebrtcP2PService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();
    private webConferenceService: BubbleConferenceServiceRB;

    private portalURL: string = "";
    private xmppHandlers: any[] = [];
    private selectedGroupId: string = null;
    private _consoleViewFilter: string;

    private subscriptions: Subscription[] = [];
    private seqNum: number = 0;

    public supervisionSubject: Subject<any>;
    public mySupervisedCalls = {};

    private supervisionSettings: SupervisionSettings;

    private supervisionGroups: SupervisionGroup[];
    private ringingSupervisedCallList: RingingSupervisedCall[] = [];

    private isMyRoleManager: boolean = false;
    private isMyRoleAssistant: boolean = false;
    private lastPickupDate: Date;

    private transferRemotePartyNumbers: string[] = [];

    private hybridCallShiftAllowed: boolean = false;

    //avoid sending same information again in case of not relevant change
    private lastMySupervisionMessageSent: string;
    private mySupervisionTimer: any;

    public static getInstance() {
        return Service.create(SUPERVISION_SVC, SupervisionService);
    }

    public static build() {
        return new SupervisionService();
    }

    protected contactService: ContactService = ContactService.getInstance();

    private constructor() {
        super();
        this.supervisionSubject = new Subject<any>();
    }

    public start() {
        this.logger.info('[SupervisionService] === STARTING ===');
        this.portalURL = config.restServerUrl + "/api/rainbow/telephonysupervision/v1.0";
        this.attachListeners();

        const featureSupervisorAllowed: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SUPERVISION_GROUP_SUPERVISOR_ALLOWED);
        const featureHybridManagerAssistant: boolean = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.HYBRID_MANAGER_ASSISTANT);

        if ((this.contactService.userContact.isRainbowHybridUser() && (featureSupervisorAllowed || featureHybridManagerAssistant))
            || this.contactService.userContact.isSupervisor()) {
            this.initSupervisionData();
        } else {
            this.supervisionGroups = [];
            this.started = true;
        }

        this.hybridCallShiftAllowed = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.TELEPHONY_HYBRID_SOFTPHONE_CALL_SHIFT) && this.settingsService.getBooleanSetting("capabilityWRGConvShift");

        //add new management for conversation shift (PBX + WEBRTC + CONFERENCE)
        if (this.hybridCallShiftAllowed || this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CALL_SHIFT)) {
            this.webConferenceService = this.mainService.inject(BUBBLE_CONFERENCE_SVC);
            this.subscriptions.push(this.conversationService.subscribe((event: RBEvent) => {
                try {
                    if (event.name === ConversationServiceEvents.ON_NEW_CALL_IN_CONVERSATION
                        || event.name === ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION) {

                        const evtConversation: ConversationRB = event.data.conversation;

                        if (evtConversation?.call && evtConversation?.call.callStatus !== CallStatus.ENDED) {
                        //add new call into the list of supervision calls
                            this.handleNewCallConversation(evtConversation);

                        } else {
                        //remove call from supervision call list if there

                        //and send update if necessary
                        }
                    }
                }
                catch {
                //do nothing
                }
            }, [ConversationServiceEvents.ON_NEW_CALL_IN_CONVERSATION, ConversationServiceEvents.ON_REMOVE_CALL_IN_CONVERSATION]));

            this.subscriptions.push(this.webrtcService.subscribe(() => {
                this.sendSnapshotToOtherResources();
            }, WebrtcP2PServiceEvents.RAINBOW_SUPERVISION_SNAPSHOT_NEEDED));

            if (this.webConferenceService) {
                this.subscriptions.push(this.webConferenceService?.subscribeToInnerEvents((event: RBEvent) => {
                    if (event?.name === WebConferenceServiceInnerEvents.ON_CONFERENCE_UPDATE_SUPERVISION_STATE) {
                        this.sendSupervisionUpdateMessage();
                    }
                }));
            }

            //in case of reconnect, we'll receive this event and we need to make snapshot to update the list of conversations
            this.subscriptions.push(this.contactService.subscribeToInnerEvents((event: RBEvent) => {
                try {
                    if (event?.name === ContactServiceInnerEvents.ON_CONNECTED_USER_JID_RESOURCE_CHANGE) {
                    //clear all calls and resend snapshot
                        if (event.data.fullJid !== this.contactService.userContact.fullJid) {
                            if (event.data.action === "remove" && this.mySupervisedCalls[event.data.fullJid]) {
                            //a ressource has been removed
                                delete this.mySupervisedCalls[event.data.fullJid];
                                this.sendEvent(SupervisionService.ON_SUPERVISION_CONVERSATIONS_UPDATED, { mySupervisedCalls: this.mySupervisedCalls });
                            }
                            else if (event.data.action === "added") {
                                this.sendSnapshotToResource(event.data.fullJid);
                            }
                        }
                    }
                }
                catch {
                //do nothing
                }
            }));

            //make snapshot to see if there're any active calls
            setTimeout(() => {
                this.sendSnapshotToOtherResources();
            }, 500);
        }

        this.logger.info(`[SupervisionService] === STARTED ===`);
    }

    public stop() {
        this.logger.info('[SupervisionService] === STOPPING ===');
        this.supervisionGroups = undefined;
        this.started = false;
        this.seqNum = 0;
        this.lastMySupervisionMessageSent = '';
        this.mySupervisedCalls = {};
        if (this.mySupervisionTimer) { clearInterval(this.mySupervisionTimer); this.mySupervisionTimer = undefined; }
        this.subscriptions.forEach((subscription) => { subscription.unsubscribe(); });
        this.subscriptions = [];
        this.logger.info('=== STOPPED ===');
    }

    public reconnect(): any {
        this.attachListeners();
        this.mySupervisedCalls = {};
        this.sendEvent(SupervisionService.ON_SUPERVISION_CONVERSATIONS_UPDATED, { mySupervisedCalls: this.mySupervisedCalls });
        if (this.mySupervisionTimer) { clearInterval(this.mySupervisionTimer); this.mySupervisionTimer = undefined; }
        this.logger.info('[SupervisionService] reconnect -- success');
    }

    private attachListeners(): void {
        this.xmppHandlers.forEach((handler: any) => { this.xmppService.connection.deleteHandler(handler); });
        this.xmppHandlers = [];
        this.xmppHandlers.push(this.xmppService.connection.addHandler((stanza: any) => { return this.onSupervisionMessage(stanza); }, "urn:xmpp:supervision:0", "message", "headline"));
        this.xmppHandlers.push(this.xmppService.connection.addHandler((stanza: any) => { return this.onSupervisionSnapshotMessage(stanza); }, "urn:xmpp:client:supervision:1", "message", "headline"));
        this.xmppHandlers.push(this.xmppService.connection.addHandler((stanza: any) => { return this.onSupervisionManagement(stanza); }, null, "message", "management"));
    }

    public subscribe(handler: any, eventNames?: string | string[]): Subscription {
        const observable = eventNames
            ? this.supervisionSubject.pipe(filter((event: RBEvent) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.supervisionSubject;

        return observable.subscribe((event: RBEvent) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[SupervisionService] handler () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(eventName: string, detail?: any): void {
        this.supervisionSubject.next({ name: eventName, data: detail });
    }

    public get consoleViewFilter(): string {
        return this._consoleViewFilter;
    }

    public set consoleViewFilter(filter: string) {
        this._consoleViewFilter = filter;
    }

    private handleNewCallConversation(evtConversation: ConversationRB): void {
        if (!evtConversation.call) return;
        if (evtConversation.call.callStatus === CallStatus.ENDED || evtConversation.call.callStatus === CallStatus.ERROR) return;

        //check if user has the rights to send supervision stuff
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CALL_SHIFT)) {
            return;
        }

        if (!this.hybridCallShiftAllowed && evtConversation.call.callType === CallType.PHONE) {
            return;
        }

        this.logger.info(`[SupervisionService] handleNewCallConversation for call ${evtConversation.call.id}`);

        const subscription: Subscription = evtConversation.call.subscribe((event: RBEvent) => {
            try {
                if (event?.name === CallEvents.ON_CALL_STATUS_CHANGE) {

                    const call: CallRB = evtConversation?.call;
                    this.logger.debug("[SupervisionService] -- " + call?.id + " -- on event " + event.name + " status: " + (call?.callStatus));

                    if (call?.callStatus === CallStatus.ENDED || call?.callStatus === CallStatus.ERROR) {
                        subscription.unsubscribe();
                    }

                    //ignore connecting state
                    if (call?.callStatus === CallStatus.CONNECTING) return;

                    //send super vision update message
                    this.sendSupervisionUpdateMessage();
                }
                if (event?.name === CallEvents.ON_CALL_MEDIA_UPDATED) {
                    const call: CallRB = evtConversation?.call;
                    this.logger.debug("[SupervisionService] -- " + call?.id + " -- on event " + event.name);

                    //send super vision update message
                    this.sendSupervisionUpdateMessage();
                }
            }
            catch (error) {
                this.logger.debug(`[SupervisionService] -- conv Id: ${evtConversation.id} call id ${evtConversation.call?.id} --  on event error -- ${error}`);
            }
        });

        //send initial state of the call
        this.sendSupervisionUpdateMessage();
    }

    public sendSupervisionUpdateMessage(fullJid?: string) {
        //if fullJid is set, the we wont broadcast the message, but send it only to the ressource given;
        //if fulljid is set, then it's a snapshot response message
        if (!this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.WEBRTC_CALL_SHIFT)) {
            return;
        }

        //get list of current webrtc calls
        let state: string = "free";
        const webrtcCalls = this.webrtcService.getCurrentCallsNotMediaPillar();

        //add under temp flag
        // const mpCalls = (localStorage.getItem("supervisionMP") === "true") ? this.telephonyService.getCalls() : [];
        const mpCalls = (this.hybridCallShiftAllowed) ? this.telephonyService.getNotIdleCalls() : [];

        const webConference = this.webConferenceService?.getActiveWebConferenceSession();
        const callsInfos = [];
        webrtcCalls.forEach((call: CallRB) => {
            if (call.callStatus !== CallStatus.ENDED) {
                let callState; let media;
                if (call.callStatus === CallStatus.RINGING_OUTGOING || call.callStatus === CallStatus.DIALING || call.callStatus === CallStatus.ERROR) { callState = "engagedOut"; }
                else if (call.callStatus === CallStatus.RINGING_INCOMMING) { callState = "ringingIn"; }
                else if (call.callStatus === CallStatus.ACTIVE) { callState = "busy"; }
                else if (call.callStatus === CallStatus.HOLD) { callState = "hold"; }
                else if (call.callStatus === CallStatus.PUT_ON_HOLD) { callState = "holdVictim"; }

                if (call.localMedia & CallMedia.AUDIO) { media = "audio"; }
                if (call.localMedia & CallMedia.VIDEO) { media = "video"; }
                if (call.localMedia & CallMedia.SHARING && media !== "video") { media = "sharing"; }

                const ressource = call.fullJid ? this.xmppService.getResourceFromJid(call.fullJid) : undefined;
                callsInfos.push({ media, callState, firstName: call.contact?.firstname, lastName: call.contact?.lastname, displayName: call.contact?.displayName, jid: call.contact?.jid, id: call.id, res: ressource, type: "user" });

                //calculate global state
                state = this.calculateSupervisionState(state, callState);
            }
        });

        mpCalls.forEach((call: CallRB) => {
            if (call.callStatus !== CallStatus.ENDED && call.mediaPillarCall?.webrtcCallRef && call.mediaPillarCall?.webrtcCallRef.callStatus !== CallStatus.ENDED) {
                let callState; let media;
                if (call.callStatus === CallStatus.RINGING_OUTGOING || call.callStatus === CallStatus.QUEUED_OUTGOING || call.callStatus === CallStatus.DIALING || call.callStatus === CallStatus.ERROR) { callState = "engagedOut"; }
                else if (call.callStatus === CallStatus.RINGING_INCOMMING || call.callStatus === CallStatus.QUEUED_INCOMMING || call.callStatus === CallStatus.ANSWERING) { callState = "ringingIn"; }
                else if (call.callStatus === CallStatus.ACTIVE) { callState = "busy"; }
                else if (call.callStatus === CallStatus.HOLD) { callState = "hold"; }
                else if (call.callStatus === CallStatus.PUT_ON_HOLD) { callState = "holdVictim"; }

                if (call.localMedia & CallMedia.AUDIO) { media = "audio"; }
                if (call.localMedia & CallMedia.VIDEO) { media = "video"; }
                if (call.localMedia & CallMedia.SHARING && media !== "video") { media = "sharing"; }

                if (callState) { // don't send undefined callState
                    //get the remote phone number
                    const phoneNumber = call.remoteNumber;
                    //get the MP call
                    const mpcall = call.mediaPillarCall.webrtcCallRef;
                    callsInfos.push({ media, callState, firstName: call.contact?.firstname, lastName: call.contact?.lastname, id: call.id, res: "mediapillar", type: "tel", jid: this.xmppService.getBareJidFromJid(mpcall.fullJid), sid: mpcall.id, displayName: call.contact?.displayName, phoneNumber });

                    //calculate global state
                    state = this.calculateSupervisionState(state, callState);
                }
            }
        });

        if (webConference?.haveJoined) {
            state = "busy";
            callsInfos.push({ id: webConference.audioSession?.sid, media: "audio", callState: "busy", roomId: webConference.id, type: "sfu", displayName: webConference.bubble.name });
        }

        if (this.lastMySupervisionMessageSent && callsInfos && !fullJid) {
            if (this.lastMySupervisionMessageSent === JSON.stringify(callsInfos)) {
                //ignore duplicate message
                this.logger.debug(`[SupervisionService] sendSupervisionUpdateMessage -- ignored as nothing has changed`);
                return;
            }
        }

        this.logger.debug(`[SupervisionService] sendSupervisionUpdateMessage`);

        this.lastMySupervisionMessageSent = JSON.stringify(callsInfos);

        this.seqNum += 1;
        const supervisionMessage = $msg({ from: this.contactService.userContact.fullJid, to: fullJid ? fullJid : this.contactService.userContact.jid, type: 'headline', id: "web_" + crypto.randomUUID() });
        if (!fullJid) {
            supervisionMessage.c("tag", { xmlns: "urn:xmpp:supervision:0" });
        }
        supervisionMessage.c("supervision", { xmlns: "urn:xmpp:client:supervision:1", state: state, ts: Date.now().toString(), seqNum: this.seqNum.toString() });

        callsInfos.forEach((infos) => {
            const endpoint = {
                jid: infos.jid,
                type: infos.type,
                res: infos.res,
                sid: infos.sid
            };

            if (infos.roomId) endpoint["roomId"] = infos.roomId;
            if (infos.firstName) endpoint["firstName"] = infos.firstName;
            if (infos.lastName) endpoint["lastName"] = infos.lastName;
            if (infos.displayName) endpoint["displayName"] = infos.displayName;
            if (infos.phoneNumber) endpoint["phoneNumber"] = infos.phoneNumber;
            supervisionMessage.c("call", { id: infos.id, state: infos.callState, media: infos.media });
            supervisionMessage.c("endpoint", endpoint).up().up();
        });

        this.xmppService.send(supervisionMessage);
    }

    private calculateSupervisionState(state: string, callState: string): string {
        if (state === "free") {
            state = callState;
        }
        else if (callState === "hold") { state = callState; }
        else if (state === "holdVictim") {
            if (callState === "ringingIn") { state = "waiting"; }
        }
        else if (state === "engagedOut" || state === "busy") {
            if (callState === "busy") { state = "busy"; }
            if (callState === "ringingIn") { state = "waiting"; }
        }
        else if (state === "ringingIn") {
            if (callState === "engagedOut" || callState === "busy") { state = "waiting"; }
        }

        return state;
    }

    private sendSnapshotToOtherResources() {
        const contact = this.contactService.userContact;
        this.logger.info(`[SupervisionService] sendSnapshotToOtherResources -- ${Object.keys(contact.resources)?.length}`);

        if (!Object.keys(contact.resources).length) {
            //we still dont have the resources ready, wait;
            setTimeout(() => {
                this.sendSnapshotToOtherResources();
            }, 1000);
            return;
        }

        Object.keys(contact.resources).forEach((key: string) => {
            this.sendSnapshotToResource(key);
        });
    }

    private sendSnapshotToResource(fullJid: string) {
        if (fullJid !== this.contactService.userContact.fullJid) {
            const resource = this.contactService.userContact.resources[fullJid];
            if (resource.type === "phone") { return; }
            if (resource.show === "online" || resource.show === "away") { return; }
            this.logger.info(`[SupervisionService] sendSnapshotToResource -- ${fullJid}`);

            this.seqNum += 1;
            const supervisionMessage = $msg({ from: this.contactService.userContact.fullJid, to: fullJid, type: 'headline', id: "web_" + crypto.randomUUID() });
            supervisionMessage.c("supervision", { xmlns: "urn:xmpp:client:supervision:1" });
            supervisionMessage.c("service");
            supervisionMessage.c("snapshot", { seqNum: this.seqNum.toString() });

            this.xmppService.send(supervisionMessage);
        }
    }

    private onSupervisionSnapshotMessage(stanza: any) {
        try {
            const stanzaElem = $(stanza);
            const tag = stanzaElem.find("tag");
            if (!tag.length) {
                //it's a snapshot message, send it for treatment
                this.logger.info("[SupervisionService] onSupervisionSnapshotMessage...");
                this.onSupervisionMessage(stanza);
            }
            return true;
        }
        catch (error) {
            this.logger.error("[SupervisionService] onSupervisionSnapshotMessage  -- failure -- " + error.message);
            return true;
        }
    }

    public async makeCallShift(conversation: ConversationRB) {
        const contactJid = conversation.contact?.jid ? conversation.contact.jid : conversation.room?.dbId;
        const pbxCallId = conversation.call?.id;

        this.logger.debug(`[SupervisionService] makeCallShift for contact jid ${contactJid} and pbx id ${pbxCallId}`);

        if (contactJid || pbxCallId) {
            let pbxCallInfos;

            Object.keys(this.mySupervisedCalls).forEach((key) => {
                //find the entry in the list of supervisions
                this.mySupervisedCalls[key]?.callInfos.forEach((callInfo: CallInfo) => {
                    //find by contact jid or by pbx call id
                    if (((contactJid && callInfo.callerJid === contactJid) || (pbxCallId && callInfo.callId === pbxCallId)) && this.mySupervisedCalls[key].state === "busy") {
                        if (callInfo.type === "sfu") { this.webConferenceService?.joinWebConference(conversation.room); }
                        else if (callInfo.type === "tel") { pbxCallInfos = callInfo; }
                        else { this.webrtcService.callShift(callInfo.callId, callInfo.callerJid + "/" + callInfo.resource); }
                    }
                });
            });

            if (pbxCallInfos) {
                //get the PBX related
                const pbxCall = this.telephonyService.calls[pbxCallInfos.callId];
                if (!pbxCall) {
                    //make snapshot ? refuse action ?
                    this.logger.error(`[SupervisionService] makeCallShift for CSTA call ${pbxCallInfos.callId} -- call not found`);
                    return;
                }

                const call = await this.webrtcService.callShiftPBX(pbxCallInfos.callId, pbxCallInfos.callerJid + "/" + pbxCallInfos.resource, pbxCallInfos.sid);

                if (!call) {
                    //something went wrong;
                    this.logger.error(`[SupervisionService] makeCallShift for CSTA call ${pbxCallInfos.callId} -- call shift error`);
                    return;
                }

                //we need to create the MP call contaxt in the MP GW to map the CSTA call and the new created WEBRTC Call
                if (this.webrtcGatewayService) {
                    const webrtcGwContext = this.webrtcGatewayService.getMediaPillarCallContext();
                    webrtcGwContext.onGoingCallMediaPillarJid = pbxCallInfos.callerJid + "/" + pbxCallInfos.resource;
                    webrtcGwContext.callState = MPCallState.CallActive;
                    webrtcGwContext.telephonyCallRefs.push(pbxCall);
                    webrtcGwContext.webrtcCallRef = call;
                    call.setMediaPillarCall(webrtcGwContext);
                    pbxCall.setMediaPillarCall(webrtcGwContext);
                    pbxCall.remoteControledHybride = false;
                }

                //if all is OK, send supervision update event
                this.sendSupervisionUpdateMessage();

                //update the call
                conversation.callShift = false;
            }
        }
    }

    private async initSupervisionData() {
        try {
            this.supervisionGroups = undefined;
            let supervisedUsers: Contact[];

            if (this.contactService.userContact?.isSupervisor()) {
                this.logger.debug(`[SupervisionService] initSupervisionData supervisor getSupervisedUsers...`);
                supervisedUsers = await this.getSupervisedUsers();
                this.logger.debug(`[SupervisionService] initSupervisionData supervisor getSupervisedUsers...done`);

                this.logger.debug(`[SupervisionService] initSupervisionData supervisor initializeSupervisionSettings...`);
                this.supervisionSettings = await this.initializeSupervisionSettings();
                this.logger.debug(`[SupervisionService] initSupervisionData supervisor initializeSupervisionSettings...done`);
            }

            this.logger.debug(`[SupervisionService] initSupervisionData getSupervisionGroups...`);
            const supervisionGroups: SupervisionGroup[] = await this.getSupervisionGroups(true);
            this.logger.debug(`[SupervisionService] initSupervisionData getSupervisionGroups...done`);

            supervisionGroups.forEach((group: SupervisionGroup) => {

                // check if I am a supervisor user in this group
                group.isMyRoleSupervisor = group.supervisorUsers.includes(this.contactService.userContact.dbId);
                // check if I am a supervised user in this group
                group.isMyRoleSupervised = group.supervisedUsers.includes(this.contactService.userContact.dbId);
                // check if I am a supervisor + admin user in this group
                group.isMyRoleSupervisorAdmin = ((group.members || []).find((member: SupervisionGroupMember) => {
                    return member.userId === this.contactService.userContact.dbId;
                }))?.roles.includes("supervisor_admin");

                this.logger.info(`[SupervisionService] initSupervisionData: group ${group.name} isMyRoleSupervisor ${group.isMyRoleSupervisor} isMyRoleSupervisorAdmin ${group.isMyRoleSupervisorAdmin} isMyRoleSupervised ${group.isMyRoleSupervised}`);

                if (group.isMyRoleSupervisor) {
                    // compute list of supervised user contacts from list of supervision user ids
                    group.supervisedUserContacts = group.supervisedUsers
                        .filter((supervisedUserId: string) => {
                            //exclude my user from the supervised list
                            return supervisedUserId !== this.contactService.userContact.dbId;
                        })
                        .map((supervisedUserId: string) => {
                            return supervisedUsers.find((supervisedUser: any) => {
                                return supervisedUser.dbId === supervisedUserId;
                            });
                        });
                }

                // Compute group avatar with initials and color
                const initials = this.buildInitials(group.name);
                const color = this.userInfoService.computeUserColor(group.name).color;
                group.avatar = this.userInfoService.createDefaultAvatarImage(initials, color).src;
            });
            this.supervisionGroups = supervisionGroups;
            this.computeMyManagerAssistantRole();

            this.started = true;
            this.sendEvent('ON_SUPERVISION_GROUP_UPDATED');
        }
        catch (error) {
            this.logger.error(`[SupervisionService] initSupervisionData -- Failure -- ${error.message}`);
        }
    }

    private computeMyManagerAssistantRole() {
        let isMyRoleManager: boolean = false;
        let isMyRoleAssistant: boolean = false;

        this.supervisionGroups.forEach((group: SupervisionGroup) => {
            const isMyRoleManagerInGroup: boolean = group.type === "manager_assistant" && group.supervisedUsers.includes(this.contactService.userContact.dbId);
            if (isMyRoleManagerInGroup) {
                isMyRoleManager = true;
            }
            const isMyRoleAssistantInGroup: boolean = group.type === "manager_assistant" && group.supervisorUsers.includes(this.contactService.userContact.dbId);
            if (isMyRoleAssistantInGroup) {
                isMyRoleAssistant = true;
            }
        });
        this.isMyRoleManager = isMyRoleManager;
        this.isMyRoleAssistant = isMyRoleAssistant;

        this.logger.info(`[SupervisionService] computeMyManagerAssistantRole -- isMyRoleManager: ${isMyRoleManager}, isMyRoleAssistant: ${isMyRoleAssistant}`);
    }

    private buildInitials(name: string) {
        const nameParts = name.split(' ');
        if (nameParts.length === 1) {
            return name.substr(0, 2).toUpperCase();
        } else if (nameParts.length > 1) {
            return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
        }
        return "";
    }

    public async getSupervisionGroups(init?: boolean): Promise<SupervisionGroup[]> {
        this.logger.debug(`[SupervisionService] getSupervisionGroups init ${init}`);

        if (this.supervisionGroups) {
            this.logger.info('[SupervisionService] getSupervisionGroups: supervision groups already initialized, returns groups nb: ' + this.supervisionGroups.length);
            return this.supervisionGroups;
        }

        if (!this.started && !init) {
            this.logger.info('[SupervisionService] getSupervisionGroups error : data not ready');
            throw new Error('data not ready');
        }

        this.logger.info('[SupervisionService] getSupervisionGroups: retrieve groups from server');
        try {
            const url = new URL(`${this.portalURL}/groups`);

            url.searchParams.set("format", "full");
            if (this.contactService.userContact.isSupervisor() && this.contactService.userContact.isRainbowHubUser()) {
                url.searchParams.set("supervisorId", this.contactService.userContact.dbId);
            } else if (this.contactService.userContact.isRainbowHybridUser()) {
                url.searchParams.set("userId", this.contactService.userContact.dbId);
            }

            const headers = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const jsonResponse: any = await response.json();
            const data: any = jsonResponse.data;

            this.logger.info('[SupervisionService] getSupervisionGroups: returns groups nb: ' + data.length);
            return data as SupervisionGroup[];
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getSupervisionGroups');
            this.logger.error(`[SupervisionService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async getSupervisionGroupsMutualAid(): Promise<SupervisionGroup[]> {
        this.logger.debug(`[SupervisionService] getSupervisionGroupsMutualAid`);

        // Hub users in groups of type "mutual aid" are not supported yet
        if (this.contactService.userContact.isRainbowHubUser()) {
            return [];
        }

        const allSupervisionGroups: SupervisionGroup[] = await this.getSupervisionGroups();
        // filter "mutual aid" type
        const filteredGroups: SupervisionGroup[] = allSupervisionGroups.filter((group: SupervisionGroup) => {
            return group.type === "mutual_aid";
        });
        return filteredGroups;
    }

    public async getSupervisionGroup(groupId: string): Promise<SupervisionGroup> {
        this.logger.info(`[SupervisionService] getSupervisionGroup ${groupId}`);

        try {
            const url = new URL(`${this.portalURL}/groups/${groupId}`);

            const headers = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const jsonResponse: any = await response.json();
            const data: any = jsonResponse.data;

            this.logger.info(`[SupervisionService] getSupervisionGroup ${groupId}: success`);
            return data as SupervisionGroup;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getSupervisionGroup');
            this.logger.error(`[SupervisionService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    private async getSupervisedUsers(groupId: string = null): Promise<Contact[]> {
        let allSupervisedUsers: Contact[] = [];
        let otherUsersToRetrieve: boolean = false;
        let page: number = 1;
        const pageSize: number = 100;
        do {
            const supervisedUsersResponse: any = await this.getSupervisedUsersPaginated(groupId, page, pageSize);
            const supervisedUsers: Contact[] = supervisedUsersResponse.supervisedUsers;
            allSupervisedUsers = allSupervisedUsers.concat(supervisedUsers);
            otherUsersToRetrieve = supervisedUsersResponse.total - (page * pageSize) > 0;
            page++;
        } while (otherUsersToRetrieve);
        return allSupervisedUsers;
    }

    private async getSupervisedUsersPaginated(groupId: string = null, page: number = 1, pageSize: number = 8): Promise<any> {
        this.logger.info(`[SupervisionService] getSupervisedUsers groupId ${groupId}`);

        try {
            const url = new URL(`${this.portalURL}/supervised`);
            url.searchParams.set("format", "full");
            url.searchParams.set("includePersonalRoutines", "true");
            url.searchParams.set("includeForwards", "true");

            if (pageSize > 0) { url.searchParams.set("limit", String(pageSize)); }
            if (page > 0 && pageSize > 0) { url.searchParams.set("offset", String(pageSize * (page - 1))); }

            if (groupId) {
                url.searchParams.set("groupId", groupId);
            }

            const headers = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            const jsonResponse: any = await response.json();
            const data: any = jsonResponse.data;

            const supervisedUsers: Contact[] = (data || []).map((supervisedUserData) => {
                return this.contactService.createContactFromData(supervisedUserData);
            });

            this.logger.info('[SupervisionService] getSupervisedUsers: returns users nb: ' + supervisedUsers.length);
            return { supervisedUsers, total: jsonResponse.total };
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'getSupervisedUsers');
            this.logger.error(`[SupervisionService] ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async pickupCall(contact: Contact, callId?: string): Promise<void> {
        let pendingPickupCallerNumber: string;
        try {
            const url = new URL(`${this.portalURL}/calls/pickup`);

            const resource: string = this.xmppService.getResourceFromJid(this.contactService.userContact.fullJid);
            const body = {
                jid_tel: contact.jidtel,
                supervisorResource: resource,
                callId
            };

            // a delay is sometimes useful between pickups so that hybrid system has the time to complete the answer to the right call (due to answer on webrtc mp call)
            if (this.lastPickupDate) {
                const now = new Date();
                const timeSinceLastPickup: number = now.getTime() - this.lastPickupDate.getTime();
                if (timeSinceLastPickup < 2000 && !this.telephonyService.getActiveCall()) {
                    const differDelay: number = 3000 - timeSinceLastPickup;
                    this.logger.info(`[SupervisionService] pickupCall delayed ${differDelay} milliseconds or active call detection`);
                    await this.waitActiveCall(differDelay);
                    await this.delay(500);
                    this.logger.info(`[SupervisionService] pickupCall after delay, ready to be executed`);
                }
            }
            this.lastPickupDate = new Date();

            pendingPickupCallerNumber = this.storePendingPickup(contact, callId);

            this.logger.info(`[SupervisionService] pickupCall ${JSON.stringify(body)}`);

            const headers = this.authService.getPostHeader();
            const response: any = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(body) });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] pickupCall success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'pickupCall');
            this.logger.error(`[SupervisionService] pickupCall error ${errorInfo.message}`);
            this.removePendingPickup(pendingPickupCallerNumber);
            throw errorInfo.error;
        }
    }

    private delay(milliseconds: number) {
        return new Promise((resolve) => {
            setTimeout(resolve, milliseconds);
        });
    }

    private waitActiveCall(timeout: number = 3000, retryPeriod: number = 300): Promise<void> {
        const start = Date.now();
        this.logger.debug("[supervisionService] waitActiveCall start -- " + start);
        return new Promise(waitForActiveCall.bind(this));

        function waitForActiveCall(resolve, reject) {
            if (this.telephonyService.getActiveCall()) {
                this.logger.debug("[supervisionService] waitActiveCall active call found");
                resolve();
            } else if (timeout && (Date.now() - start) >= timeout) {
                this.logger.debug("[supervisionService] waitActiveCall timeout reached " + timeout);
                resolve();
            } else {
                this.logger.debug("[supervisionService] waitActiveCall no active call yet");
                setTimeout(waitForActiveCall.bind(this, resolve, reject), retryPeriod);
            }
        }
    }

    private storePendingPickup(contact: Contact, callId: string): string {
        if (this.telephonyService.isOxe && contact && callId) {
            this.logger.debug(`[supervisionService] storePendingPickup contact id  ${contact?.id} callId ${callId}`);

            // retrieve supervised call entry
            const supervisedCall: RingingSupervisedCall = this.ringingSupervisedCallList.find((ringingSupervisedCall: RingingSupervisedCall) => {
                return ringingSupervisedCall.supervisedContact.id === contact.id && ringingSupervisedCall.callInfo?.callId === callId;
            });
            if (supervisedCall) {
                this.logger.debug(`[supervisionService] storePendingPickup found supervisedCall callId ${supervisedCall.callInfo?.callId} on user ${supervisedCall.supervisedContact?.id}`);
                const callerNumber: string = supervisedCall.callInfo.callerNumber || "";
                if (callerNumber) {
                    this.logger.debug(`[supervisionService] storePendingPickup store callerNumber ${callerNumber}`);
                    this.telephonyService.setAutoAnswerCorrelatorIdResourceMap(callerNumber);
                    return callerNumber;
                }
            } else {
                this.logger.debug(`[supervisionService] storePendingPickup no supervisedCall found with contact id  ${contact?.id} callId ${callId}`);
            }
        }
        return null;
    }

    private removePendingPickup(callerNumber: string) {
        if (this.telephonyService.isOxe && callerNumber) {
            this.telephonyService.removeAutoAnswerCorrelatorId(callerNumber);
        }
    }

    public setSelectedGroupId(id: string): void {
        this.selectedGroupId = id;
    }

    public getSelectedGroupId(): string {
        return this.selectedGroupId;
    }

    public async initializeSupervisionSettings(): Promise<SupervisionSettings> {
        try {
            const url = new URL(`${this.portalURL}/settings`);

            this.logger.info(`[SupervisionService] initializeSupervisionSettings`);

            const headers = this.authService.getRequestHeader();
            const response: any = await fetch(url.toString(), { method: 'GET', headers });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] initializeSupervisionSettings success`);

            const jsonResponse: any = await response.json();
            const data: SupervisionSettings = jsonResponse.data as SupervisionSettings;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'initializeSupervisionSettings');
            this.logger.error(`[SupervisionService] initializeSupervisionSettings error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async updateSupervisionSettings(disableSupervisionCallNotificationsOnMobiles: boolean): Promise<SupervisionSettings> {
        try {
            const url = new URL(`${this.portalURL}/settings`);

            this.logger.info(`[SupervisionService] updateSupervisionSettings`);

            const headers = this.authService.getPostHeader();
            const body = {
                disableSupervisionCallNotificationsOnMobiles
            };

            const response: any = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(body) });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] updateSupervisionSettings success`);

            const jsonResponse: any = await response.json();
            const data: SupervisionSettings = jsonResponse.data as SupervisionSettings;
            return data;
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'updateSupervisionSettings');
            this.logger.error(`[SupervisionService] updateSupervisionSettings error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public getSupervisionSettings(): SupervisionSettings {
        return this.supervisionSettings;
    }

    //*************************************************//
    //* Supervision Management Events                 *//
    //*************************************************//

    private async addOrUpdateSupervisionGroup(groupId: string): Promise<boolean> {
        let groupAdded = false;
        try {
            const supervisionGroup: SupervisionGroup = await this.getSupervisionGroup(groupId);

            // check if I am a supervisor user in this group
            supervisionGroup.isMyRoleSupervisor = supervisionGroup.supervisorUsers.includes(this.contactService.userContact.dbId);
            // check if I am a supervised user in this group
            supervisionGroup.isMyRoleSupervised = supervisionGroup.supervisedUsers.includes(this.contactService.userContact.dbId);
            // check if I am a supervisor + admin user in this group
            supervisionGroup.isMyRoleSupervisorAdmin = ((supervisionGroup.members || []).find((member: SupervisionGroupMember) => {
                return member.userId === this.contactService.userContact.dbId;
            }))?.roles.includes("supervisor_admin");

            if (!supervisionGroup.isMyRoleSupervisor && !supervisionGroup.isMyRoleSupervised) {
                // Ignore group if I am nor supervisor neither supervised
                // This is possible if I am the admin of the company (administrator are also notified by the server of group changes)
                this.logger.info(`[SupervisionService] addSupervisionGroup: nor supervisor neither supervised => ignore group ${supervisionGroup.name} isMyRoleSupervisor ${supervisionGroup.isMyRoleSupervisor} isMyRoleSupervisorAdmin ${supervisionGroup.isMyRoleSupervisorAdmin} isMyRoleSupervised ${supervisionGroup.isMyRoleSupervised}`);
                return false;
            }

            this.logger.info(`[SupervisionService] addSupervisionGroup: group ${supervisionGroup.name} isMyRoleSupervisor ${supervisionGroup.isMyRoleSupervisor} isMyRoleSupervisorAdmin ${supervisionGroup.isMyRoleSupervisorAdmin} isMyRoleSupervised ${supervisionGroup.isMyRoleSupervised}`);

            if (supervisionGroup.isMyRoleSupervisor) {
                const allMySupervisedUsers: Contact[] = await this.getSupervisedUsers(groupId);

                // compute list of supervised user contacts from list of supervised user identifiers
                supervisionGroup.supervisedUserContacts = supervisionGroup.supervisedUsers
                    .filter((supervisedUserId: string) => {
                        //exclude my user from the supervised list
                        return supervisedUserId !== this.contactService.userContact.dbId;
                    })
                    .map((supervisedUserId: string) => {
                        return allMySupervisedUsers.find((supervisedUser: any) => {
                            return supervisedUser.dbId === supervisedUserId;
                        });
                    });
            }

            // Compute group avatar with initials and color
            const initials = this.buildInitials(supervisionGroup.name);
            const color = this.userInfoService.computeUserColor(supervisionGroup.name).color;
            supervisionGroup.avatar = this.userInfoService.createDefaultAvatarImage(initials, color).src;

            // Initialize this.supervisionGroups, if necessary
            // this.supervisionGroups may be undefined during startup: initialize it upon insertion of our user in a supervision group
            // this could happen in rare usecases when groups are modified during user initialization
            this.supervisionGroups = this.supervisionGroups || [];

            // add supervisor role if necessary
            if (!this.contactService.userContact.isSupervisor() && supervisionGroup.isMyRoleSupervisor) {
                // user becomes supervisor, but no event is coming from server to notify this new role:
                // we change it in our local cache of userContact
                this.contactService.userContact.roles.push(UserRole.GROUP_SUPERVISOR);
                this.contactService.userContact.supervisorConsoleEnabled = this.profileService.isFeatureEnabled(this.profileService.FeaturesEnum.SUPERVISION_GROUP_SHOW_CONSOLE);
                this.supervisionSettings = await this.initializeSupervisionSettings();
                this.sendEvent('ON_SUPERVISOR_ROLE_UPDATED');
            }

            // add or update group in supervision group array
            const existingGroupIndex = this.supervisionGroups.findIndex((group) => {
                return group.id === groupId;
            });

            if (existingGroupIndex !== -1) {
                // group already exists: (I was present as supervised): replace existing group by new one
                this.logger.debug("[SupervisionService] addSupervisionGroup add groupId " + groupId);
                this.supervisionGroups.splice(existingGroupIndex, 1, supervisionGroup);
                this.computeMyManagerAssistantRole();
                this.sendEvent('ON_SUPERVISION_GROUP_UPDATED', { groupId: groupId });
            } else {
                // add new group
                this.logger.debug("[SupervisionService] addSupervisionGroup update groupId " + groupId);
                this.supervisionGroups.push(supervisionGroup);
                this.computeMyManagerAssistantRole();
                groupAdded = true;
                this.sendEvent('ON_SUPERVISION_GROUP_CREATED', { groupId: groupId });
            }
        }
        catch (error) {
            this.logger.error(`[SupervisionService] initSupervisionData -- Failure -- ${error.message}`);
        }
        return groupAdded;
    }

    private removeSupervisionGroup(groupId: string): void {
        const existingGroupIndex = this.supervisionGroups.findIndex((group) => {
            return group.id === groupId;
        });

        if (existingGroupIndex !== -1) {
            this.logger.debug("[SupervisionService] removeSupervisionGroup remove groupId " + groupId);
            // Group exists in our cache: remove it
            this.supervisionGroups.splice(existingGroupIndex, 1);
            this.computeMyManagerAssistantRole();
            this.sendEvent('ON_SUPERVISION_GROUP_DELETED', { groupId: groupId });
        }
    }

    private removeMySelfFromMembers(groupId: string, myMemberId: string, role: string): boolean {
        let groupDeleted = false;
        const group: SupervisionGroup = this.supervisionGroups.find((supervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            // Remove myself from group's supervised users or supervisor users
            this.removeUserFromGroup(groupId, myMemberId, role);

            // compute again my roles that may have been changed
            group.isMyRoleSupervisor = group.supervisorUsers.includes(this.contactService.userContact.dbId);
            group.isMyRoleSupervised = group.supervisedUsers.includes(this.contactService.userContact.dbId);

            if (!group.isMyRoleSupervisor && !group.isMyRoleSupervised) {
                // I'm no more a supervisor or supervised: I don't belong anymore to this group
                this.removeSupervisionGroup(groupId);
                groupDeleted = true;
            } else {
                this.sendEvent('ON_SUPERVISION_GROUP_UPDATED', { groupId: groupId });
            }

            // check if the user role supervisor has to be modified
            const amISupervisorInAGroup: boolean = this.supervisionGroups.some((supervisionGroup) => {
                return supervisionGroup.isMyRoleSupervisor;
            });
            if (!amISupervisorInAGroup) {
                // user is no more supervisor: remove supervisor role from userContact
                const supervisorRoleIndex = this.contactService.userContact.roles.indexOf(UserRole.GROUP_SUPERVISOR);
                if (supervisorRoleIndex !== -1) {
                    this.contactService.userContact.roles.splice(supervisorRoleIndex, 1);
                    this.contactService.userContact.supervisorConsoleEnabled = false;
                    this.sendEvent('ON_SUPERVISOR_ROLE_UPDATED');
                }
            }
        }
        return groupDeleted;
    }

    private updateSupervisionGroupName(groupId: string, name: string): boolean {
        let updated = false;
        const group = this.supervisionGroups.find((supervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            const oldGroupName = group.name;
            this.logger.debug("[SupervisionService] updateSupervisionGroupName groupId " + groupId + " name: " + name);
            group.name = name;
            updated = (oldGroupName !== group.name);
        }
        return updated;
    }

    private updateSupervisionEmptyGroupAllowed(groupId: string, isEmptyGroupAllowed: boolean): boolean {
        let updated = false;
        const group = this.supervisionGroups.find((supervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            const oldGroupEmptyAllowed = group.isEmptyAllowed;
            this.logger.debug("[SupervisionService] updateSupervisionGroupName groupId " + groupId + " isEmptyGroupAllowed: " + isEmptyGroupAllowed);
            group.isEmptyAllowed = isEmptyGroupAllowed;
            updated = (oldGroupEmptyAllowed !== group.isEmptyAllowed);
        }
        return updated;
    }

    private removeUserFromGroup(groupId: string, userIdToRemove: string, role: string): boolean {
        let updated = false;
        const group: SupervisionGroup = this.supervisionGroups.find((supervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            // 1 - remove from supervisor/ed ids list
            const memberIds: string[] = role === "supervisor" ? group.supervisorUsers : group.supervisedUsers;
            const existingMemberIdIndex: number = (memberIds || []).findIndex((memberId) => {
                return memberId === userIdToRemove;
            });
            if (existingMemberIdIndex !== -1) {
                // remove userId from ids list
                this.logger.debug(`[SupervisionService] removeUserFromGroup groupId: ${groupId} userId: ${userIdToRemove} role: ${role} :removed from ids`);
                memberIds.splice(existingMemberIdIndex, 1);
                updated = true;
            }

            // 2 - remove from supervisor/ed contact list
            const memberUserContacts: Contact[] = role === "supervisor" ? group.supervisorUserContacts : group.supervisedUserContacts;
            const existingMemberContactIndex: number = (memberUserContacts || []).findIndex((memberUserContact) => {
                return memberUserContact.dbId === userIdToRemove;
            });
            if (existingMemberContactIndex !== -1) {
                this.logger.debug(`[SupervisionService] removeUserFromGroup groupId: ${groupId} userId: ${userIdToRemove} role: ${role} :removed from contacts`);
                memberUserContacts.splice(existingMemberContactIndex, 1);
                updated = true;
            }

            // 3 - remove from members list (except if it is still present with another role)
            const otherRoleMemberIds: string[] = role === "supervisor" ? group.supervisedUsers : group.supervisorUsers;
            const existWithOtherRole: boolean = (otherRoleMemberIds || []).some((otherRoleMemberId) => {
                return otherRoleMemberId === userIdToRemove;
            });
            if (!existWithOtherRole) {
                const existingMemberIndex: number = (group.members || []).findIndex((member: SupervisionGroupMember) => {
                    return member.userId === userIdToRemove;
                });
                if (existingMemberIndex !== -1) {
                    this.logger.debug(`[SupervisionService] removeUserFromGroup groupId: ${groupId} userId: ${userIdToRemove} role: ${role} :removed from members`);
                    group.members.splice(existingMemberIndex, 1);
                    updated = true;
                }
            }

            this.computeMyManagerAssistantRole();
        }
        return updated;
    }

    private updateGroupMember(groupId: string, memberId: string, role: string, status: string, inOutPermission: string, isDelegationEnabled: boolean): boolean {
        this.logger.debug(`[SupervisionService] updateGroupMember groupId: ${groupId} userId: ${memberId} roles: ${role} status: ${status} inOutPermission: ${inOutPermission}`);
        let updated: boolean = false;

        const group: SupervisionGroup = this.supervisionGroups.find((supervisionGroup: SupervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            const memberToUpdate: SupervisionGroupMember = (group.members || []).find((member: SupervisionGroupMember) => {
                return member.userId === memberId;
            });

            if (memberToUpdate) {
                updated = updated || (memberToUpdate.inOutPermission !== inOutPermission);
                memberToUpdate.inOutPermission = inOutPermission;

                updated = updated || (memberToUpdate.status !== status);
                memberToUpdate.status = status;

                updated = updated || (memberToUpdate.isDelegationEnabled !== isDelegationEnabled);
                memberToUpdate.isDelegationEnabled = isDelegationEnabled;

                // Roles handling
                if (role === "supervised") {
                    // ensure member roles array contains "supervised" value
                    if (!memberToUpdate.roles.includes("supervised")) {
                        // really needed?
                        memberToUpdate.roles.push("supervised");
                        updated = true;
                    }
                } else if (role === "supervisor") {
                    if (!memberToUpdate.roles.includes("supervisor")) {
                        // first remove supervisor_admin role in member roles array (if the member has this role)
                        const supervisorAdminIndex: number = memberToUpdate.roles.findIndex((memberRole: string) => {
                            return memberRole === "supervisor_admin";
                        });
                        if (supervisorAdminIndex !== -1) {
                            memberToUpdate.roles.splice(supervisorAdminIndex, 1);
                        }
                        // Then add supervisor role in member roles array
                        memberToUpdate.roles.push("supervisor");

                        // Update group.isMyRoleSupervisorAdmin if the updated member is myself
                        if (memberToUpdate.userId === this.contactService.userContact.dbId) {
                            group.isMyRoleSupervisorAdmin = false;
                            updated = true;
                        }
                    }
                } else if (role === "supervisor_admin") {
                    if (!memberToUpdate.roles.includes("supervisor_admin")) {
                        // first remove supervisor role in member roles array (if the member has this role)
                        const supervisorIndex: number = memberToUpdate.roles.findIndex((memberRole: string) => {
                            return memberRole === "supervisor";
                        });
                        if (supervisorIndex !== -1) {
                            memberToUpdate.roles.splice(supervisorIndex, 1);
                        }
                        // Then add supervisor role in member roles array
                        memberToUpdate.roles.push("supervisor_admin");

                        // Update group.isMyRoleSupervisorAdmin if the updated member is myself
                        if (memberToUpdate.userId === this.contactService.userContact.dbId) {
                            group.isMyRoleSupervisorAdmin = true;
                            updated = true;
                        }
                    }
                }
            }
        }
        return updated;
    }

    private onSupervisionManagement(stanza: any): boolean {
        try {
            const stanzaElem = $(stanza);
            const supervisiongroupElem = stanzaElem.find("supervisiongroup");

            if (supervisiongroupElem.length > 0) {
                this.logger.info("[SupervisionService] onSupervisionManagement event for supervisiongroup...");

                const groupId = supervisiongroupElem.attr("id");
                const groupAction = supervisiongroupElem.attr("action");
                this.logger.info(`[SupervisionService] onSupervisionManagement group ${groupId} action ${groupAction}`);

                switch (groupAction) {
                    case "create":
                    case "update": {
                        let addOrUpdateSupervisionGroupNeeded: boolean = false;
                        let groupCreatedOrDeleted: boolean = false;
                        let needNotifyUpdate: boolean = false;
                        const supervisorElements = supervisiongroupElem.find("supervisor");
                        if (supervisorElements.length > 0) {
                            supervisorElements.each((__ind, supervisor) => {
                                const supervisorElem = $(supervisor);
                                const supervisorId = supervisorElem.attr("id");
                                const supervisorAction = supervisorElem.attr("action");
                                this.logger.debug("[SupervisionService] onSupervisionManagement supervisor id: " + supervisorId + " action: " + supervisorAction);

                                if (supervisorId === this.contactService.userContact.dbId) {
                                    this.logger.debug(`[SupervisionService] onSupervisionManagement myself supervisor ${supervisorAction} in supervisor list`);

                                    switch (supervisorAction) {
                                        case "add":
                                            // I am added as a supervisor in a group:
                                            // Add (or update if I was already present as supervised) this group in our supervision groups
                                            addOrUpdateSupervisionGroupNeeded = true;
                                            break;
                                        case "remove":
                                            // I am removed as supervisor from the group
                                            // 1: let's update or remove all supervised calls linked to this supervisor group
                                            this.cleanSupervisedCallsForSupervisorGroup(groupId);
                                            // 2: update group config data
                                            groupCreatedOrDeleted = this.removeMySelfFromMembers(groupId, supervisorId, "supervisor");
                                            break;
                                        case "update": {
                                            // my role or status or inOutPermission has changed
                                            const role: string = supervisorElem.attr("roles");
                                            const status: string = supervisorElem.attr("status");
                                            const inOutPermission: string = supervisorElem.attr("inOutPermission");
                                            const isDelegationEnabled: boolean = supervisorElem.attr("isDelegationEnabled") === "true";
                                            const updatedMember: boolean = this.updateGroupMember(groupId, supervisorId, role, status, inOutPermission, isDelegationEnabled);
                                            needNotifyUpdate = needNotifyUpdate || updatedMember;

                                            if (status === "idle") {
                                                // I have left this group
                                                // let's update or remove all supervised calls linked to this supervisor group
                                                this.cleanSupervisedCallsForSupervisorGroup(groupId);
                                            }
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                } else {
                                    this.logger.debug(`[SupervisionService] onSupervisionManagement supervisor ${supervisorAction} in supervisor list`);

                                    switch (supervisorAction) {
                                        case "add":
                                            // A supervisor is added in the group:
                                            addOrUpdateSupervisionGroupNeeded = true;
                                            break;
                                        case "remove": {
                                            // A supervisor is removed from the group:
                                            const removeDone = this.removeUserFromGroup(groupId, supervisorId, "supervisor");
                                            needNotifyUpdate = needNotifyUpdate || removeDone;
                                            break;
                                        }
                                        case "update": {
                                            // my role or status or inOutPermission has changed
                                            const role: string = supervisorElem.attr("roles");
                                            const status: string = supervisorElem.attr("status");
                                            const inOutPermission: string = supervisorElem.attr("inOutPermission");
                                            const isDelegationEnabled: boolean = supervisorElem.attr("isDelegationEnabled") === "true";
                                            const updatedMember: boolean = this.updateGroupMember(groupId, supervisorId, role, status, inOutPermission, isDelegationEnabled);
                                            needNotifyUpdate = needNotifyUpdate || updatedMember;
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                }
                            });
                        }

                        // check if there are supervisedUsers changes
                        const supervisedElements = supervisiongroupElem.find("supervised");
                        if (supervisedElements.length > 0) {
                            supervisedElements.each((__ind, supervised) => {
                                const supervisedElem = $(supervised);
                                const supervisedId = supervisedElem.attr("id");
                                const supervisedAction = supervisedElem.attr("action");
                                this.logger.debug("[SupervisionService] onSupervisionManagement supervised id: " + supervisedId + " action: " + supervisedAction);

                                if (supervisedId === this.contactService.userContact.dbId) {
                                    this.logger.debug(`[SupervisionService] onSupervisionManagement I am ${supervisedAction === "add" ? "added" : (supervisedAction === "remove" ? "removed" : "unknown action")} in supervised list`);

                                    switch (supervisedAction) {
                                        case "add":
                                            // I am added as a supervised user in a group:
                                            // Add (or update if I was already present as supervisor) this group in our supervision groups
                                            addOrUpdateSupervisionGroupNeeded = true;
                                            break;
                                        case "remove":
                                            // I am removed as supervised from the group
                                            groupCreatedOrDeleted = this.removeMySelfFromMembers(groupId, supervisedId, "supervised");
                                            break;
                                        case "update": {
                                            // my role or status or inOutPermission has changed
                                            const role: string = supervisedElem.attr("roles");
                                            const status: string = supervisedElem.attr("status");
                                            const inOutPermission: string = supervisedElem.attr("inOutPermission");
                                            const isDelegationEnabled: boolean = supervisedElem.attr("isDelegationEnabled") === "true";
                                            const updatedMember: boolean = this.updateGroupMember(groupId, supervisedId, role, status, inOutPermission, isDelegationEnabled);
                                            needNotifyUpdate = needNotifyUpdate || updatedMember;
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                } else {
                                    switch (supervisedAction) {
                                        case "add":
                                            // A new supervised user is added in the group
                                            addOrUpdateSupervisionGroupNeeded = true;
                                            break;
                                        case "remove": {
                                            // A supervised user is removed from the group
                                            // 1: let's update or remove all supervised calls linked to this user
                                            this.cleanSupervisedCallsForSupervisedUser(supervisedId, groupId);
                                            // 2: update config data
                                            const removeDone = this.removeUserFromGroup(groupId, supervisedId, "supervised");
                                            needNotifyUpdate = needNotifyUpdate || removeDone;
                                            break;
                                        }
                                        case "update": {
                                            // A supervised user's role or status or inOutPermission or isDelegationEnabled has changed
                                            const role: string = supervisedElem.attr("roles");
                                            const status: string = supervisedElem.attr("status");
                                            const inOutPermission: string = supervisedElem.attr("inOutPermission");
                                            const isDelegationEnabled: boolean = supervisedElem.attr("isDelegationEnabled") === "true";
                                            const updatedMember: boolean = this.updateGroupMember(groupId, supervisedId, role, status, inOutPermission, isDelegationEnabled);
                                            needNotifyUpdate = needNotifyUpdate || updatedMember;

                                            if (status === "idle") {
                                                // a supervised has left a group
                                                // let's update or remove all supervised calls linked to this user
                                                this.cleanSupervisedCallsForSupervisedUser(supervisedId, groupId);
                                            }
                                            break;
                                        }
                                        default:
                                            break;
                                    }
                                }
                            });
                        }

                        if (addOrUpdateSupervisionGroupNeeded) {
                            this.addOrUpdateSupervisionGroup(groupId);
                            groupCreatedOrDeleted = true;
                        }

                        if (!groupCreatedOrDeleted) {
                            // check if there is a group name change
                            const nameElement = supervisiongroupElem.find("name");
                            if (nameElement.length > 0) {
                                const name = nameElement.text();
                                const updateDone = this.updateSupervisionGroupName(groupId, name);
                                needNotifyUpdate = needNotifyUpdate || updateDone;
                            }

                            // check if there is a emptyAllowed propery change
                            const emptyAllowedElement = supervisiongroupElem.find("is-empty-allowed");
                            if (emptyAllowedElement.length > 0) {
                                const isEmptyAllowed: string = emptyAllowedElement.text();
                                const updateDone = this.updateSupervisionEmptyGroupAllowed(groupId, isEmptyAllowed === "true");
                                needNotifyUpdate = needNotifyUpdate || updateDone;
                            }

                            if (needNotifyUpdate) {
                                this.sendEvent('ON_SUPERVISION_GROUP_UPDATED', { groupId: groupId });
                            }
                        }
                    }
                        break;
                    case "delete":
                        this.removeSupervisionGroup(groupId);
                        break;
                    default:
                        break;
                }

            }

            const supervisionsettingsElem = stanzaElem.find("supervisionsettings");
            if (supervisionsettingsElem.length > 0) {
                const settingAction: string = supervisionsettingsElem.attr("action");

                switch (settingAction) {
                    default:
                    case "update":
                        this.logger.info(`[SupervisionService] onSupervisionManagement supervisionsettings action ${settingAction} disableSupervisionCallNotificationsOnMobiles ${supervisionsettingsElem.find("disableSupervisionCallNotificationsOnMobiles").text()}`);
                        this.supervisionSettings.disableSupervisionCallNotificationsOnMobiles = supervisionsettingsElem.find("disableSupervisionCallNotificationsOnMobiles").text() === "true";
                        break;
                }
            }

            return true;
        }
        catch (error) {
            this.logger.error("[SupervisionService] onManagementMessage  -- failure -- " + error.message);
            return true;
        }
    }

    private cleanSupervisedCallsForSupervisorGroup(groupId: string) {
        this.logger.info(`[SupervisionService] cleanSupervisedCallsForSupervisorGroup for groupId ${groupId}`);

        const group: SupervisionGroup = this.supervisionGroups.find((supervisionGroup: SupervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        if (group) {
            // A) reset supervisionState and call info for all supervised users of this group if the supervised user is not supervised anymore (it may be supervised in another group)
            group.supervisedUserContacts.forEach((supervisedUserContact: Contact) => {
                this.resetSupervisionDataForSupervisedUserIfNecessary(supervisedUserContact, groupId);
            });

            // B) clear group incoming supervised calls counter
            group.nbIncomingRingingCalls = 0;
            this.sendEvent('ON_SUPERVISION_GROUP_RINGING_CALLS_UPDATED', { group });

            // C) Update global list of supervised calls

            // C1 - remove group id, group name, group avatar for all calls of the global list
            this.removeGroupItemsForCalls(group, this.ringingSupervisedCallList);

            // C2 - remove calls having an empty groupIds
            this.removeSupervisedCallsHavingNoGroupIds();

            this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_CONTACTS_UPDATED, { ringingSupervisedCallList: this.ringingSupervisedCallList, op: "create_or_delete" });
        }
    }

    private cleanSupervisedCallsForSupervisedUser(supervisedUserId: string, groupId: string) {
        this.logger.info(`[SupervisionService] cleanSupervisedCallsForSupervisedUser for supervised user id ${supervisedUserId} of group ${groupId}`);

        const group: SupervisionGroup = this.supervisionGroups.find((supervisionGroup: SupervisionGroup) => {
            return supervisionGroup.id === groupId;
        });

        const supervisedUserContact: Contact = group.supervisedUserContacts.find((groupSupervisedUserContact: Contact) => {
            return groupSupervisedUserContact.dbId === supervisedUserId;
        });

        if (group) {
            // A) reset supervisionState and call info for this supervised user if he is not supervised anymore (it may be supervised in another group)
            this.resetSupervisionDataForSupervisedUserIfNecessary(supervisedUserContact, groupId);

            // B) update group's incoming supervised calls counter
            // compute number of incoming calls in this group: this attribute is used to set red icon marker on the group tab in supervision console to notify ringing call(s) in the group
            const prevNbGroupRingingCalls: number = group.nbIncomingRingingCalls;

            group.nbIncomingRingingCalls = group.supervisedUserContacts
                .filter((supervisedUserContact: Contact) => {
                    if (group.type !== "mutual_aid") return true;
                    const supervisedMemberInGroup: SupervisionGroupMember = group.members.find((member: SupervisionGroupMember) => {
                        return member.userId === supervisedUserContact.dbId;
                    });
                    return supervisedMemberInGroup?.status === "active";
                })
                .reduce((accumulator: number, supervisedUserContact: any) => {
                    const userNbIncomingCalls: number = (supervisedUserContact.supervisionCallInfos || []).reduce((accumulator: number, callInfo: CallInfo) => {
                        return accumulator + (callInfo.callState === "ringingIn" ? 1 : 0);
                    }, 0);
                    this.logger.debug(`[SupervisionService] cleanSupervisedUserCalls user: ${supervisedUserContact.phonePbx} userNbIncomingCalls: ${userNbIncomingCalls}`);
                    return accumulator + userNbIncomingCalls;
                }, 0);

            if (group.nbIncomingRingingCalls !== prevNbGroupRingingCalls) {
                this.sendEvent('ON_SUPERVISION_GROUP_RINGING_CALLS_UPDATED', { group });
            }

            // C) Modify global list of supervised calls

            // C1 - remove group id, group name, group avatar for all calls of the logoff supervised User
            const supervisedUserRingingCalls: RingingSupervisedCall[] = (this.ringingSupervisedCallList || [])
                .filter((ringingSupervisedCall: RingingSupervisedCall) => {
                    return ringingSupervisedCall.supervisedContact.dbId === supervisedUserId;
                });
            this.removeGroupItemsForCalls(group, supervisedUserRingingCalls);

            // C2 - remove calls having an empty groupIds
            this.removeSupervisedCallsHavingNoGroupIds();

            this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_CONTACTS_UPDATED, { ringingSupervisedCallList: this.ringingSupervisedCallList, op: "create_or_delete" });
        }
    }

    private resetSupervisionDataForSupervisedUserIfNecessary(supervisedUserContact: Contact, groupId: string) {
        // reset supervisionState and call info of the supervised user of the group groupId if the supervised user is not supervised anymore (it may be supervised in another group)
        const isUserSupervisedByAnotherGroup: boolean = this.supervisionGroups.some((supervisionGroup: SupervisionGroup) => {
            if (supervisionGroup.id === groupId) return false;
            const myselfSupervisorInGroup: boolean = supervisionGroup.isMyRoleSupervisor;
            if (myselfSupervisorInGroup) {
                // Am i logged in group ?
                let myLoggedStatusInGroup: string;
                if (supervisionGroup.type === "mutual_aid") {
                    const myselfMemberInGroup: SupervisionGroupMember = supervisionGroup.members.find((member: SupervisionGroupMember) => {
                        return member.userId === this.contactService.userContact.dbId;
                    });
                    myLoggedStatusInGroup = myselfMemberInGroup?.status; //check that I am active supervisor in mutual aid group
                } else {
                    myLoggedStatusInGroup = "active"; // for groups type different from "mutual_aid", consider we are active
                }
                return myLoggedStatusInGroup === "active" && supervisionGroup.supervisedUsers.includes(supervisedUserContact.dbId);
            }
            return false;
        });

        if (!isUserSupervisedByAnotherGroup) {
            // the supervised user is not supervised anymore
            supervisedUserContact.resetSupervisionState();
            supervisedUserContact.resetSupervisionCallinfos();
            // Compute supervision capabilities
            this.telephonyService.computeAndNotifySupervisionCapabilities(supervisedUserContact, true);
        }
    }

    private removeGroupItemsForCalls(group: SupervisionGroup, ringingSupervisedCallList: RingingSupervisedCall[]) {
        // Remove group id from groupIds, group name from groupsNames, group avatar from groupsAvatars for all calls of the give callList
        return (ringingSupervisedCallList || []).map((ringingSupervisedCall: RingingSupervisedCall) => {
            //remove group id from groupsIds
            if (ringingSupervisedCall.groupsIds.includes(group.id)) {
                const index: number = ringingSupervisedCall.groupsIds.findIndex(groupsId => groupsId === group.id);
                if (index !== -1) {
                    ringingSupervisedCall.groupsIds.splice(index, 1);
                }
            }
            //remove group name from groupsNames
            if (ringingSupervisedCall.groupsNames.includes(group.name)) {
                const index: number = ringingSupervisedCall.groupsNames.findIndex(groupsName => groupsName === group.name);
                if (index !== -1) {
                    ringingSupervisedCall.groupsNames.splice(index, 1);
                }
            }
            //remove group avatar from groupsAvatars
            if (ringingSupervisedCall.groupsAvatars.includes(group.avatar)) {
                const index: number = ringingSupervisedCall.groupsAvatars.findIndex(groupsAvatar => groupsAvatar === group.avatar);
                if (index !== -1) {
                    ringingSupervisedCall.groupsAvatars.splice(index, 1);
                }
            }
            return ringingSupervisedCall;
        });
    }

    private removeSupervisedCallsHavingNoGroupIds() {
        const removedSupervisedCalls: RingingSupervisedCall[] = [];

        this.ringingSupervisedCallList = (this.ringingSupervisedCallList || []).filter((ringingSupervisedCall: RingingSupervisedCall) => {
            const keepSupervisedCall: boolean = ringingSupervisedCall.groupsIds.length !== 0;
            // keep ringingSupervisedCall if groupsIds not empty
            if (!keepSupervisedCall) {
                removedSupervisedCalls.push(ringingSupervisedCall);
            }
            return keepSupervisedCall;
        });

        // notify removed supervision calls for supervised call popup cleaning
        if (removedSupervisedCalls.length) {
            removedSupervisedCalls.forEach((ringingSupervisedCall: RingingSupervisedCall) => {
                this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_SUPERVISED_CALL_REMOVED, { ringingSupervisedCall });
            });
        }
    }

    //*****************************//
    //* Supervision State Events  *//
    //*****************************//

    /**
     * Method onSupervisionMessage
     * @private
     * @memberof SupervisionService
     * Handler of supervision messages
     * @param {any} stanza the xmpp event stanza
     * @returns {boolean} true when event handling completed
     */
    private onSupervisionMessage(stanza: any): boolean {
        try {
            this.logger.info("[SupervisionService] onSupervisionMessage...");
            const stanzaElem = $(stanza);

            const userJid: string = stanzaElem.attr("from");
            let myUserSupervision: boolean = false;
            if (userJid && this.contactService.isUserContactJid(userJid)) {
                if (userJid === this.contactService.userContact.fullJid) { this.logger.info("[SupervisionService][MyUserSuperv] onSupervisionMessage from myself -- ignore"); return true; }
                this.logger.info("[SupervisionService][MyUserSuperv] onSupervisionMessage from my user");
                myUserSupervision = true;
            }

            const forwardedElements = stanzaElem.find('forwarded[xmlns="urn:xmpp:supervision:0"]');
            this.logger.info(`[SupervisionService] onSupervisionMessage forwardedElements nb: ${forwardedElements.length}`);

            if (forwardedElements.length > 0) {
                // the message is a supervision message broadcasted by ejabberd from a supervised user to a supervisor
                this.logger.info(`[SupervisionService] onSupervisionMessage supervision message broadcasted by ejabberd from a supervised user to a supervisor`);

                forwardedElements.each((_ind, forwarded) => {
                    const forwardedElem = $(forwarded);
                    this.handleSupervisionElement(forwardedElem, myUserSupervision, userJid);
                });
            } else {
                // the message is a direct supervision message (used in user's devices supervision for call shift feature)
                this.logger.info(`[SupervisionService] onSupervisionMessage direct supervision message for my user devices supervision`);
                this.handleSupervisionElement(stanzaElem, myUserSupervision, userJid);
            }

            return true;
        }
        catch (error) {
            this.logger.error("[SupervisionService] onSupervisionMessage  -- failure -- " + error.message);
            return true;
        }
    }

    private handleSupervisionElement(element, myUserSupervision: boolean, userJid: string) {

        // read supervised user in "delay" element (for supervision message broadcasted by ejabberd)
        let userJidTel: string;
        const delayElem = element.find("delay");
        if (delayElem.length > 0) {
            userJidTel = delayElem.attr("from");
            this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage from supervised user jidTel: " + userJidTel);
        }

        // Manage supervision state change and call info update
        const supervisionElem = element.find("supervision");
        if (supervisionElem.length > 0) {
            // read supervision state, device and callinfo
            const state = supervisionElem.attr("state");
            const device: string = supervisionElem.attr("device");
            const update: boolean = supervisionElem.attr("update") === "true";
            this.logger.info(`[SupervisionService] onSupervisionMessage state: ${state} device: ${device}`);

            // read supervision calls
            const callInfos: CallInfo[] = [];
            const callElements = supervisionElem.find("call");
            callElements.each((_ind, call) => {
                const callElem = $(call);
                const callId = callElem.attr("id");
                const callState = callElem.attr("state");
                const callerNumber = callElem.attr("callerid");
                let callerName = callElem.attr("callername");
                const callerFirstname = callElem.attr("firstname");
                const callerLastname = callElem.attr("lastname");
                const callType: string = callElem.attr("type");

                if (callerNumber && (callerName || callerFirstname || callerLastname)) {
                    const callerContact: Contact = this.contactService.createBasicContact(null, callerNumber, false);
                    if (callerFirstname || callerLastname) {
                        const updatedFirstname = callerFirstname ? callerFirstname : '';
                        const updatedLastname = callerLastname ? callerLastname : '';
                        callerContact.firstname = updatedFirstname;
                        callerContact.lastname = updatedLastname;
                        callerContact.computeDisplayName();
                        callerName = callerContact.displayName;
                        this.logger.debug(`[SupervisionService][GroupSuperv] onSupervisionMessage call id: ${callId} callerNumber: ${callerNumber} callerName: '${callerName}' computed from firstname '${callerFirstname}' lastname '${callerLastname}'`);
                    } else {
                        this.logger.debug(`[SupervisionService][GroupSuperv] onSupervisionMessage call id: ${callId} callerNumber: ${callerNumber} callerName: '${callerName}' taken as is because no firstname / lastname present`);
                    }
                }

                const callInfo: CallInfo = { callId, callState, callerNumber, callerName, callType };

                if (myUserSupervision) {
                    let callerJid = callElem.find("endpoint").attr("roomId");
                    if (!callerJid) callerJid = callElem.find("endpoint").attr("jid");
                    const res = callElem.find("endpoint").attr("res");
                    const type = callElem.find("endpoint").attr("type");
                    const sessionId = callElem.find("endpoint").attr("sid");
                    const media = callElem.attr("media");
                    callInfo.callerJid = callerJid;
                    callInfo.resource = res;
                    callInfo.media = media;
                    callInfo.type = type;
                    callInfo.sid = sessionId;
                    callInfos.push(callInfo);
                    this.logger.debug(`[SupervisionService][MyUserSuperv] onSupervisionMessage call id: ${callId} state: ${callState} callerJid: ${callerJid} resource: ${res} media: ${media} type: ${type}`);
                } else {
                    callInfos.push(callInfo);
                    this.logger.debug(`[SupervisionService][GroupSuperv] onSupervisionMessage callInfo: ${JSON.stringify(callInfo)}`);
                }
            });

            const snapshotElement = supervisionElem.find("snapshot");
            if (snapshotElement.length) {
                this.logger.debug(`[SupervisionService][MyUserSuperv] onSupervisionMessage snapshot demand is received from ${userJid}`);
                this.sendSupervisionUpdateMessage(userJid);
                return true;
            }

            if (userJidTel && state) {
                if (!update) {
                    this.notifySupervisionState(userJidTel, state, device, callInfos);
                } else {
                    this.notifySupervisionCallUpdate(userJidTel, callInfos);
                }
            }

            if (myUserSupervision) {
                //update the current supervision state for my user
                this.onMyUserSupervisionCallsUpdated(userJid, state, callInfos);
            }
        }

        // Manage forward data (Voice Hub)
        const forwardUpdatedElem = element.find("forwardUpdated");
        if (userJidTel && forwardUpdatedElem.length > 0) {
            const forwardType: string = forwardUpdatedElem.attr("forwardType");
            this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage Voice forward forwardType: " + forwardType);

            if (forwardType === "immediate") {
                const activate: string = forwardUpdatedElem.attr("activate");
                const destinationType: string = forwardUpdatedElem.attr("destinationType");
                const destinationName: string = forwardUpdatedElem.attr("destinationName");

                this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage forwardUpdated activate: " + activate);
                this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage forwardUpdated destinationType: " + destinationType);
                this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage forwardUpdated destinationName: " + destinationName);

                this.notifySupervisionVoiceForwardImm(userJidTel, activate === "true", destinationType, destinationName);
            }
        }

        // Manage forward data (Hybrid)
        const callserviceElem = element.find("callservice");
        if (userJidTel && callserviceElem.length > 0) {
            const forwardedElem = callserviceElem.find("forwarded");
            if (forwardedElem.length > 0) {
                const forwardType: string = forwardedElem.attr("forwardType");
                const pbxForwardType: string = forwardedElem.attr("pbxForwardType");
                const forwardTo: string = forwardedElem.attr("forwardTo");

                this.logger.info(`[SupervisionService][GroupSuperv] onSupervisionMessage Hybrid forward forwardType: ${forwardType} pbxForwardType: ${pbxForwardType} forwardTo: ${forwardTo}`);
                this.notifySupervisionHybridForward(userJidTel, forwardType, pbxForwardType, forwardTo);
            }
        }

        // Manage routines data
        const personalRoutinesModificationElem = element.find("personalRoutinesModification");
        if (userJidTel && personalRoutinesModificationElem.length > 0) {
            const action: string = personalRoutinesModificationElem.attr("action");
            this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage personalRoutinesModification action: " + action);

            switch (action) {
                case "activate": {
                    const routineId: string = personalRoutinesModificationElem.attr("routineId");
                    this.logger.info("[SupervisionService][GroupSuperv] onSupervisionMessage personalRoutinesModification routineId: " + routineId);
                    this.notifySupervisionRoutineActive(userJidTel, routineId);
                    break;
                }
                case "create":
                case "delete":
                case "update":
                    this.notifySupervisionRoutineListModified(userJidTel);
                    break;
                default:
                    this.logger.warn(`[SupervisionService][GroupSuperv] onSupervisionMessage personalRoutinesModification for action '${action}' not handled`);
                    break;
            }
        }
    }

    public notifySupervisionState(userFullJidTel: string, state: string, device: string, callInfos: CallInfo[]) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact: Contact = this.contactService.getContactByJidTel(jidTel);
        if (contact
            && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            // Notify supervision state change

            this.logger.debug("[SupervisionService] notifySupervisionState -- handle supervision state change on user: " + contact.phonePbx + " jidtel: " + contact.jidtel + " state: " + state);

            if (!device) {
                // No supervision device in sipwise supervision (the user supervision state is computed by the server)
                contact.supervisionState = state;
            } else {
                // Devices are used in hybrid supervision (main and secondary devices):
                // The server returns a supervision state for main and secondary devices, and the webclient computes the
                // resulting user supervision state based on the 2 devices.
                // Note that only one device is used if the user has only a deskphone or only a remote extension.

                const supervisionDevices: string[] = contact.getSupervisionDevices();
                // if (!supervisionDevices.length) {
                //     this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState -- current supervision device numbers on user: '${contact.phonePbx}': none`);
                // } else {
                //     supervisionDevices.forEach(supervisionDevice => {
                //         this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState -- current supervision device: '${supervisionDevice}' => '${contact.getSupervisionDeviceState(supervisionDevice)}'`);
                //     });
                // }

                if (supervisionDevices.length > 0 && !supervisionDevices.includes(device)) {
                    // Ignore supervision event for another device if one device already exists
                    this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState -- IGNORE supervision event for secondary device: ${device} on user: '${contact.phonePbx}' event state: ${state}`);
                    return;
                }

                contact.setSupervisionDeviceState(device, state);
                this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState setSupervisionDeviceState -- device '${device}' state '${state}' => updated user state '${contact.supervisionState}'`);

                // supervisionDevices = contact.getSupervisionDevices();
                // if (!supervisionDevices.length) {
                //     this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState -- updated supervision device numbers on user: '${contact.phonePbx}': none`);
                // } else {
                //     supervisionDevices.forEach(supervisionDevice => {
                //         this.logger.debug(`[SupervisionService][DEVICE] notifySupervisionState -- updated supervision device: '${supervisionDevice}' => '${contact.getSupervisionDeviceState(supervisionDevice)}'`);
                //     });
                // }
            }

            contact.supervisionCallInfos = callInfos;

            this.logger.debug(`[SupervisionService][SUPERV] notifySupervisionState updated contact callInfos: ${contact.supervisionCallInfos ? JSON.stringify(contact.supervisionCallInfos) : ''}`);
            this.sendEvent('ON_SUPERVISION_CONTACT_CALLINFOS_UPDATED', { contact }); // this event is sent in order to play the audio notification in the supervisor webclient for incoming call on supervised users

            // Compute supervision capabilities
            this.telephonyService.computeAndNotifySupervisionCapabilities(contact, true);

            const groupsContainingUser: SupervisionGroup[] = [];

            // Compute and notify number of incoming ringing calls for all groups containing this user whose supervision state has changed
            (this.supervisionGroups || []).forEach((group: SupervisionGroup) => {

                // Am i logged in group ?
                let myLoggedStatusInGroup: string;
                if (group.type === "mutual_aid") {
                    const myselfMemberInGroup: SupervisionGroupMember = group.members.find((member: SupervisionGroupMember) => {
                        return member.userId === this.contactService.userContact.dbId;
                    });
                    myLoggedStatusInGroup = myselfMemberInGroup?.status; //check that I am active in mutual aid group
                } else {
                    myLoggedStatusInGroup = "active"; // for groups type different from "mutual_aid", consider we are active
                }

                // Does the group contain the user whose supervision state has changed in its supervised users list ?
                const isUserInGroup = myLoggedStatusInGroup === "active"
                    && (group.supervisedUserContacts || []).some((supervisedUserContact: Contact) => {
                        return supervisedUserContact.jid === contact.jid;
                    });
                if (isUserInGroup) {
                    // check if supervised user is "active" in group
                    let supervisedUserLoggedStatusInGroup: string;
                    if (group.type === "mutual_aid") {
                        const supervisedUserMemberInGroup: SupervisionGroupMember = group.members.find((member: SupervisionGroupMember) => {
                            return member.userId === contact.dbId;
                        });
                        supervisedUserLoggedStatusInGroup = supervisedUserMemberInGroup?.status; //check that the supervised user is active in mutual aid group
                    } else {
                        supervisedUserLoggedStatusInGroup = "active"; // for groups type different from "mutual_aid", consider the supervised user is active
                    }

                    if (supervisedUserLoggedStatusInGroup === "active") {
                        groupsContainingUser.push(group);

                        // compute number of incoming calls in this group: this attribute is used to set red icon marker on the group tab in supervision console to notify ringing call(s) in the group
                        const prevNbGroupRingingCalls: number = group.nbIncomingRingingCalls;

                        group.nbIncomingRingingCalls = group.supervisedUserContacts.reduce((accumulator: number, supervisedUserContact: any) => {
                            const userNbIncomingCalls: number = (supervisedUserContact.supervisionCallInfos || []).reduce((accumulator: number, callInfo: CallInfo) => {
                                return accumulator + (callInfo.callState === "ringingIn" ? 1 : 0);
                            }, 0);
                            this.logger.debug(`[SupervisionService] notifySupervisionState user: ${supervisedUserContact.phonePbx} userNbIncomingCalls: ${userNbIncomingCalls}`);
                            return accumulator + userNbIncomingCalls;
                        }, 0);

                        if (group.nbIncomingRingingCalls !== prevNbGroupRingingCalls) {
                            this.sendEvent('ON_SUPERVISION_GROUP_RINGING_CALLS_UPDATED', { group });
                        }
                    }
                }
            });

            // Compute and notify global list of incoming ringing calls on all supervised contacts
            // These data are used by ringing supervised area component
            let isListModified: boolean = false;
            const ringingCallInfos: CallInfo[] = []; // list of ringing callInfo of the supervised contact

            // First add ringing call(s) of the supervised contact to the global list (if not already present)
            if (contact.supervisionState === "ringingIn" || contact.supervisionState === "waiting") {
                // The supervised contact contains at least one incoming ringing call:
                // Add his incoming ringing calls to global ringingSupervisedCallList (if not already present)
                (callInfos || []).forEach((callInfo) => {
                    // Ignore call from myself
                    if (callInfo?.callerNumber === this.contactService.userContact.phoneInternalNumber) {
                        this.logger.debug("[SupervisionService] notifySupervisionState -- ignore call to supervised user from myself: callId: " + callInfo.callId + " myself: " + callInfo.callerNumber + " to supervised: " + contact.phonePbx);
                        return;
                    }

                    // Add ringing call to the global ringing supervised call list ringingSupervisedCallList (if not already present)
                    if (callInfo.callState === "ringingIn") {
                        ringingCallInfos.push(callInfo);

                        const callAlreadyExistInList: boolean = this.ringingSupervisedCallList.some((ringingSupervisedCall: RingingSupervisedCall) => ringingSupervisedCall.callInfo?.callId === callInfo.callId && ringingSupervisedCall.supervisedContact?.id === contact.id);
                        if (!callAlreadyExistInList) {
                            const groupsAvatars: string[] = groupsContainingUser.map((group: SupervisionGroup) => {
                                return group.avatar;
                            });
                            const groupsNames: string[] = groupsContainingUser.map((group: SupervisionGroup) => {
                                return group.name;
                            });

                            const groupsIds: string[] = groupsContainingUser.map((group: SupervisionGroup) => {
                                return group.id;
                            });

                            this.logger.debug("[SupervisionService] notifySupervisionState -- add ringingsupervised call: " + callInfo.callId + " state: " + callInfo.callState + " in list for user " + contact.phonePbx);
                            this.logger.debug("[SupervisionService] notifySupervisionState groups ids : " + groupsIds.join(",") + " groups names: " + groupsNames.join(","));

                            const newRingingSupervisedCall: RingingSupervisedCall = {
                                supervisedContact: contact,
                                callInfo: {
                                    callId: callInfo.callId,
                                    callState: callInfo.callState,
                                    callerNumber: callInfo.callerNumber,
                                    callerName: callInfo.callerName
                                },
                                groupsAvatars,
                                groupsNames,
                                groupsIds
                            };

                            this.ringingSupervisedCallList.push(newRingingSupervisedCall);
                            isListModified = true;
                            this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_SUPERVISED_CALL_CREATED, { ringingSupervisedCall: newRingingSupervisedCall });
                        }
                    }
                });
            }

            // Then remove calls from the list for this supervised contact which are no more in the current list of user's ringing callInfos

            const removedSupervisedCalls: RingingSupervisedCall[] = [];
            this.ringingSupervisedCallList = (this.ringingSupervisedCallList || []).filter((ringingSupervisedCall: RingingSupervisedCall) => {
                if (ringingSupervisedCall.supervisedContact.id === contact.id) {
                    // keep this ringing call of the concerned supervised contact if it is still ringing
                    const isCallStillRinging: boolean = ringingCallInfos.some((ringingCallInfo) => {
                        return ringingCallInfo.callId === ringingSupervisedCall.callInfo?.callId;
                    });
                    if (!isCallStillRinging) {
                        isListModified = true;
                        removedSupervisedCalls.push(ringingSupervisedCall);
                        // Don't remove the pickup pending data here: this event notifying the supervisee free state may occur before the resulting delivered event requiring the pickup pending data
                        this.logger.debug("[SupervisionService] notifySupervisionState -- remove ringingSupervisedCall -- " + ringingSupervisedCall.callInfo?.callId + " no more ringing from list for user " + ringingSupervisedCall.supervisedContact.phonePbx);
                    }
                    return isCallStillRinging;
                }
                return true; // keep ringing calls of other supervised contacts
            });

            if (isListModified) {
                this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_CONTACTS_UPDATED, { ringingSupervisedCallList: this.ringingSupervisedCallList, op: "create_or_delete" });
            }

            if (removedSupervisedCalls.length) {
                removedSupervisedCalls.forEach((ringingSupervisedCall: RingingSupervisedCall) => {
                    this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_SUPERVISED_CALL_REMOVED, { ringingSupervisedCall });
                });
            }
        }
    }

    public notifySupervisionCallUpdate(userFullJidTel: string, callInfos: CallInfo[]) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact: Contact = this.contactService.getContactByJidTel(jidTel);
        if (contact
            && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            // Notify supervision state change

            this.logger.debug("[SupervisionService] notifySupervisionCallUpdate -- handle supervision call update on user: " + contact.phonePbx + " jidtel: " + contact.jidtel);

            let isListModified: boolean = false;

            if (contact.supervisionState === "ringingIn" || contact.supervisionState === "waiting") {
                // The supervised contact contains at least one incoming ringing call:
                // update the call info from event callInfo
                (callInfos || []).forEach((callInfo) => {
                    if (callInfo.callState === "ringingIn") {
                        const existingCallToUpdate: RingingSupervisedCall = this.ringingSupervisedCallList.find((ringingSupervisedCall: RingingSupervisedCall) => {
                            return ringingSupervisedCall.supervisedContact.id === contact.id && ringingSupervisedCall.callInfo?.callId === callInfo.callId;
                        });

                        // update the caller displayname of the found call
                        if (existingCallToUpdate) {
                            existingCallToUpdate.callInfo.callerName = callInfo.callerName;
                            contact.updateSupervisionCallInfo(callInfo);
                            isListModified = true;
                            this.sendEvent(SupervisionService.ON_SUPERVISION_RINGING_SUPERVISED_CALL_CREATED, { ringingSupervisedCall: existingCallToUpdate });
                        }
                    }
                });
                this.logger.debug(`[SupervisionService][SUPERV] notifySupervisionCallUpdate updated contact callInfos: ${contact.supervisionCallInfos ? JSON.stringify(contact.supervisionCallInfos) : ''}`);
            }

            if (isListModified) {
                this.sendEvent('ON_SUPERVISION_RINGING_CONTACTS_UPDATED', { ringingSupervisedCallList: this.ringingSupervisedCallList, op: "update" });
            }
        }
    }

    public notifySupervisionVoiceForwardImm(userFullJidTel: string, activate: boolean, destinationType: string, destinationName: string) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact = this.contactService.getContactByJidTel(jidTel);
        if (contact && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            // Notify supervised contact's immediate forward change
            contact.forwards = contact.forwards.map((forward: TelephonyForward) => {
                return forward.type !== "immediate" ? forward : forward.updateFromVoiceEventData(activate, destinationType, destinationName);
            });
        }
    }

    public notifySupervisionHybridForward(userFullJidTel: string, forwardType: string, pbxForwardType: string, forwardTo: string) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact = this.contactService.getContactByJidTel(jidTel);
        if (contact && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            // Notify supervised contact's forward change
            contact.forwards = [TelephonyForward.createForwardFromHybridData({ forwardType, pbxForwardType, forwardTo })];
        }
    }

    public notifySupervisionRoutineActive(userFullJidTel: string, routineId: string) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact = this.contactService.getContactByJidTel(jidTel);
        if (contact && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            // Notify supervised contact's active routine change
            contact.personalRoutines = contact.personalRoutines.map((routine: PersonalRoutine) => {
                routine.active = (routine.id === routineId);
                return routine;
            });
        }
    }

    public async notifySupervisionRoutineListModified(userFullJidTel: string) {
        const jidTel = this.xmppService.getBareJidFromJid(userFullJidTel);
        const contact = this.contactService.getContactByJidTel(jidTel);

        if (contact && contact.jid !== this.contactService.userContact.jid) { // ignore events if I am myself the supervisee
            try {
                const personalRoutinesData = await this.personalRoutinesService.retrieveAllPersonalRoutinesForContactFromServer(contact.dbId);
                this.contactService.updateContactPersonalRoutines(contact.jid, personalRoutinesData);

            } catch (error) {
                this.logger.warn(`[SupervisionService] notifySupervisionRoutineCreated - issue with contact routine update : ${error.message}`);
            }
        }
    }

    public getRingingSupervisedCallList(): RingingSupervisedCall[] {
        return this.ringingSupervisedCallList;
    }

    private onMyUserSupervisionCallsUpdated(userJid: string, state: string, callInfos: CallInfo[] = []) {
        //save events from all our ressources
        this.mySupervisedCalls[userJid] = {
            state,
            callInfos
        };

        //in case of PBX calls, mark them as remote controlled so that we can hide them from the UI part
        callInfos.forEach((infos: CallInfo) => {
            if (infos.type === "tel" && this.telephonyService.calls[infos.callId] && infos.callState !== "ringingIn") {
                this.telephonyService.calls[infos.callId].remoteControledHybride = true;
            }
        });

        //in case the user gets into a call, arm a checking timer to see if things have changed. As other party might get disconnected, or crash app or whatever,
        //we'll never get an event on that, It's based on the user presence, if online then reset all blocked calls;
        if (callInfos?.length) {
            //start watcher if not already done
            if (!this.mySupervisionTimer) { this.mySupervisionTimer = setInterval(() => {
                //check if the callInfos is still not empty but my presence is now "online", then remove all infos
                if (callInfos?.length && this.contactService.userContact.status === "online") {
                    this.mySupervisedCalls = {};
                    this.sendEvent(SupervisionService.ON_SUPERVISION_CONVERSATIONS_UPDATED, { mySupervisedCalls: this.mySupervisedCalls });
                    clearInterval(this.mySupervisionTimer);
                    this.mySupervisionTimer = undefined;
                }
            }, 30000); }
        }
        else {
            if (this.mySupervisionTimer) { clearInterval(this.mySupervisionTimer); this.mySupervisionTimer = undefined; }
        }

        this.sendEvent(SupervisionService.ON_SUPERVISION_CONVERSATIONS_UPDATED, { mySupervisedCalls: this.mySupervisedCalls });
    }

    // Login / Logout of "Mutual Aid" groups

    public async setGroupLoginStatus(groupId: string, userId: string, logged: boolean): Promise<void> {
        try {
            const url = new URL(`${this.portalURL}/groups/${groupId}/members/${userId}`);

            const body = {
                status: (logged ? "active" : "idle")
            };
            this.logger.info(`[SupervisionService] setGroupLoginStatus ${JSON.stringify(body)}`);

            const headers = this.authService.getPostHeader();
            const response: any = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(body) });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] setGroupLoginStatus success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setGroupLoginStatus');
            this.logger.error(`[SupervisionService] setGroupLoginStatus error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    // Manager / Assistant

    public existManagerAssistantGroup(): boolean {
        return (this.supervisionGroups || []).some((group: SupervisionGroup) => {
            return group.type === "manager_assistant";
        });
    }

    public isMyselfManagerOrAssistant(): boolean {
        return this.isMyRoleManager || this.isMyRoleAssistant;
    }

    public isMyselfManager(): boolean {
        return this.isMyRoleManager;
    }

    public isMyselfAssistant(): boolean {
        return this.isMyRoleAssistant;
    }

    public isUserMyManager(userId: string): boolean {
        const isMyselfAssistant: boolean = this.isMyselfAssistant();
        if (isMyselfAssistant) {
            return (this.supervisionGroups || []).some((group: SupervisionGroup) => {
                return group.type === "manager_assistant"
                    && group.supervisorUsers.includes(this.contactService.userContact.dbId) // I am assistant (supervisor) in this group
                    && group.supervisedUsers.includes(userId); // userid is the manager (supervised user) of this group
            });
        }
        return false;
    }

    public async setGroupManagerCallDelegation(groupId: string, managerId: string, activated: boolean): Promise<void> {
        try {
            const url = new URL(`${this.portalURL}/groups/${groupId}/members/${managerId}`);

            const body = {
                isDelegationEnabled: activated
            };

            this.logger.info(`[SupervisionService] setGroupManagerCallDelegation ${JSON.stringify(body)}`);

            const headers = this.authService.getPostHeader();
            const response: any = await fetch(url.toString(), { method: 'PUT', headers, body: JSON.stringify(body) });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] setGroupManagerCallDelegation success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'setGroupManagerCallDelegation');
            this.logger.error(`[SupervisionService] setGroupManagerCallDelegation error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async assistantJoinAllManagerAssistantGroups(): Promise<void> {
        try {
            this.logger.info(`[SupervisionService] assistantJoinAllManagerAssistantGroups`);

            const url = new URL(`${this.portalURL}/groups/join`);
            url.searchParams.set("type", "manager_assistant");

            const headers = this.authService.getPostHeader();
            const response: any = await fetch(url.toString(), { method: 'PUT', headers });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] assistantJoinAllManagerAssistantGroups success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'assistantJoinAllManagerAssistantGroups');
            this.logger.error(`[SupervisionService] assistantJoinAllManagerAssistantGroups error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public async assistantLeaveAllManagerAssistantGroups(): Promise<void> {
        try {
            this.logger.info(`[SupervisionService] assistantLeaveAllManagerAssistantGroups`);

            const url = new URL(`${this.portalURL}/groups/leave`);
            url.searchParams.set("type", "manager_assistant");

            const headers = this.authService.getPostHeader();
            const response: any = await fetch(url.toString(), { method: 'PUT', headers });
            if (!response.ok) {
                throw (response);
            }
            this.logger.info(`[SupervisionService] assistantLeaveAllManagerAssistantGroups success`);
        }
        catch (error) {
            const errorInfo = await this.errorHelperService.getErrorInfo(error, 'assistantLeaveAllManagerAssistantGroups');
            this.logger.error(`[SupervisionService] assistantLeaveAllManagerAssistantGroups error ${errorInfo.message}`);
            throw errorInfo.error;
        }
    }

    public isManagerCallDelegationEnabled(): boolean {
        if (this.isMyselfManager()) {
            const myManagerAssistantGroup: SupervisionGroup = this.supervisionGroups.find((group: SupervisionGroup) => {
                return group.type === "manager_assistant" && group.supervisedUsers.includes(this.contactService.userContact.dbId);
            });
            // search for my member in the group
            const myselfMember: SupervisionGroupMember = myManagerAssistantGroup.members.find((member: SupervisionGroupMember) => {
                return member.userId === this.contactService.userContact.dbId;
            });
            return myselfMember?.isDelegationEnabled;
        }
        return false;
    }

    public existIncomingCallToSupervisedManager(): boolean {
        if (this.isMyselfAssistant()) {
            return this.ringingSupervisedCallList.some((ringingSupervisedCall: RingingSupervisedCall) => {
                return this.isUserMyManager(ringingSupervisedCall.supervisedContact.dbId);
            });
        }
        return false;
    }

    public isCallFromAssistant(call: CallRB): boolean {
        if (this.isMyselfManager()) {
            const myManagerAssistantGroup: SupervisionGroup = this.supervisionGroups.find((group: SupervisionGroup) => {
                return group.type === "manager_assistant" && group.supervisedUsers.includes(this.contactService.userContact.dbId);
            });
            return call.contact?.dbId && myManagerAssistantGroup?.supervisorUsers?.includes(call.contact.dbId);
        }
        return false;
    }

    public isCallTransferredByAssistant(call: CallRB): boolean {
        if (this.isMyselfManager()) {
            const myManagerAssistantGroup: SupervisionGroup = this.supervisionGroups.find((group: SupervisionGroup) => {
                return group.type === "manager_assistant" && group.supervisedUsers.includes(this.contactService.userContact.dbId);
            });
            return call.transferInitiatorUserId && myManagerAssistantGroup?.supervisorUsers?.includes(call.transferInitiatorUserId);
        }
        return false;
    }

    public storeTransferRemotePartyNumber(phoneNumber: string): void {
        if (!this.transferRemotePartyNumbers.includes(phoneNumber)) {
            this.transferRemotePartyNumbers.push(phoneNumber);

            setTimeout(() => {
                this.removeRemotePartyTransfer(phoneNumber);
            }, 3000);
        }
    }

    private removeRemotePartyTransfer(phoneNumber: string): void {
        if (this.transferRemotePartyNumbers.includes(phoneNumber)) {
            this.transferRemotePartyNumbers.splice(this.transferRemotePartyNumbers.indexOf(phoneNumber), 1);
        }
    }

    public isRemotePartyTransferred(phoneNumber: string): boolean {
        return this.transferRemotePartyNumbers.includes(phoneNumber);
    }
}
